import PrimitiveParser from "./PrimitivesParser";
import ResultAnalyzer from "./ResultAnalyzer";
import ContactHelper from "./ContactHelper";
import {default as PrimitiveConfig} from "./PrimitiveConfig";
import AssessmentAnalyzer from "./AssessmentAnalyzer";
import { io } from 'socket.io-client';
import { Unpackr } from 'msgpackr';
import CollectionUtils from "./CollectionHelper";
import { findFilterMatches, pickAtRandom } from "./SharedTransforms";
import { progress } from "framer-motion";
import { values } from "lodash";
import { resolvePrimitiveImageUrl } from "./utils/primitiveImage";

const RAW_PRIMITIVES = Symbol('rawPrimitives');
const RAW_REFERENCE_PARAMETERS = Symbol('rawReferenceParameters');
const RAW_METRICS = Symbol('rawMetrics');
const MASTER_TYPE = Symbol('primitiveMasterType');
const TITLE_VALUE = Symbol('titleValue');
const TITLE_SKIP_FLAG = Symbol('titleSkipFlag');

export function _uniquePrimitives(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return [];
    }

    const ids = new Set(); // Track unique ids
    const result = []; // Store unique primitives

    for (let i = 0; i < list.length; i++) {
        const p = list[i];
        if (p === undefined || p.id === undefined) {
            continue; // Skip undefined primitives or those without an id
        }
        const pid = p.id; // Cache the id
        if (!ids.has(pid)) {
            ids.add(pid); // Add id to set
            result.push(p); // Add the object to the result array
        }
    }

    return result; // Return the array of unique primitives
}

export function uniquePrimitives(list = []) {
    const seen = Object.create(null);
    const result = [];
  
    for (const p of list) {
      if (!p) continue;
      const pid = p.id;
  
      if (!seen[pid]) {
        seen[pid] = true;
        result.push(p);
      }
    }
  
    return result;
  }


const getPrimitiveStore = (primitive) => {
    if (!primitive) {
        return undefined
    }
    if (Object.prototype.hasOwnProperty.call(primitive, RAW_PRIMITIVES)) {
        return primitive[RAW_PRIMITIVES]
    }
    const descriptor = Object.getOwnPropertyDescriptor(primitive, 'primitives')
    if (descriptor && descriptor.get === undefined) {
        return descriptor.value
    }
    return undefined
}

const proxySourceCache = new WeakMap();
const proxyResolutionStack = new WeakSet();

function resolveProxySourcePrimitive(primitive, obj) {
    if (!primitive) {
        return undefined;
    }
    let proxyOwner = primitive;
    const visitedIds = new Set();
    while (proxyOwner && proxyOwner.type === "view" && !proxyOwner.referenceParameters?.proxy) {
        if (visitedIds.has(proxyOwner.id)) {
            return undefined;
        }
        visitedIds.add(proxyOwner.id);
        proxyOwner = proxyOwner.configParent;
    }
    if (!proxyOwner || proxyOwner.type !== "view" || !proxyOwner.referenceParameters?.proxy) {
        return undefined;
    }
    const cached = proxySourceCache.get(proxyOwner);
    if (cached) {
        return cached;
    }
    if (proxyResolutionStack.has(primitive)) {
        return undefined;
    }
    proxyResolutionStack.add(primitive);
    try {
        let candidate;
        try {
            const list = primitive.itemsForProcessingWithOptions(undefined, { bypassProxy: true }) || [];
            candidate = list.find(item => item?.type === "view") ?? list[0];
        } catch (error) {
            console.warn?.("Failed to resolve proxy source via itemsForProcessing", error);
        }
        if (!candidate || candidate === primitive) {
            return undefined;
        }
        proxySourceCache.set(proxyOwner, candidate);
        return candidate;
    } finally {
        proxyResolutionStack.delete(primitive);
    }
}

const ACTIVE_PROCESSING_STATUSES = new Set(["running", "pending", "queued"]);

function parseTimestampValue(value) {
    if (!value) {
        return -Infinity;
    }
    const asDate = new Date(value);
    const ts = asDate.getTime();
    return Number.isFinite(ts) ? ts : -Infinity;
}

function selectGenericProcessingProgress(processing = {}) {
    if (!processing || typeof processing !== "object") {
        return undefined;
    }
    const candidates = [];
    for (const [key, value] of Object.entries(processing)) {
        if (!value || typeof value !== "object") {
            continue;
        }
        if (key === "flow" || key === "run_step") {
            continue;
        }
        const progress = value.progress;
        if (progress === undefined || progress === null) {
            continue;
        }
        const status = String(value.status ?? "").toLowerCase();
        const isActive = ACTIVE_PROCESSING_STATUSES.has(status);
        const recency = Math.max(
            parseTimestampValue(value.running),
            parseTimestampValue(value.started),
            parseTimestampValue(value.pending),
            parseTimestampValue(value.updated),
            parseTimestampValue(value.completed)
        );
        candidates.push({
            key,
            progress,
            isActive,
            recency,
        });
    }
    if (candidates.length === 0) {
        return undefined;
    }
    candidates.sort((a, b) => {
        if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
        }
        if (a.recency !== b.recency) {
            return b.recency - a.recency;
        }
        return a.key.localeCompare(b.key);
    });
    return candidates[0]?.progress;
}

function progressToMessage(progress) {
    if (progress === undefined || progress === null) {
        return undefined;
    }
    if (typeof progress === "string") {
        return progress;
    }
    if (typeof progress === "number" && Number.isFinite(progress)) {
        const pct = Math.max(0, Math.min(1, progress)) * 100;
        return `${Math.round(pct)}%`;
    }
    if (typeof progress === "object") {
        if (typeof progress.message === "string" && progress.message.trim().length) {
            return progress.message;
        }
        if (typeof progress.text === "string" && progress.text.trim().length) {
            return progress.text;
        }
        if (typeof progress.percentage === "number" && Number.isFinite(progress.percentage)) {
            const pct = Math.max(0, Math.min(1, progress.percentage)) * 100;
            return `${Math.round(pct)}%`;
        }
    }
    return undefined;
}

function progressToPercentage(progress) {
    if (progress === undefined || progress === null) {
        return undefined;
    }
    if (typeof progress === "number" && Number.isFinite(progress)) {
        return Math.max(0, Math.min(1, progress > 1 ? progress / 100 : progress));
    }
    if (typeof progress === "object") {
        if (typeof progress.percentage === "number" && Number.isFinite(progress.percentage)) {
            return Math.max(0, Math.min(1, progress.percentage > 1 ? progress.percentage / 100 : progress.percentage));
        }
    }
    return undefined;
}

function mergeAxisSettings(base = {}, override = {}) {
    return {
        ...(base ?? {}),
        ...(override ?? {})
    };
}

function mergeExploreConfig(baseExplore = {}, overrideExplore = {}) {
    const baseFilters = Array.isArray(baseExplore.filters) ? baseExplore.filters : [];
    const overrideFilters = Array.isArray(overrideExplore.filters) ? overrideExplore.filters : [];
    const maxFilters = Math.max(baseFilters.length, overrideFilters.length);
    const mergedFilters = [];
    for (let i = 0; i < maxFilters; i += 1) {
        if (baseFilters[i] || overrideFilters[i]) {
            mergedFilters[i] = {
                ...(baseFilters[i] ?? {}),
                ...(overrideFilters[i] ?? {}),
            };
        }
    }
    const mergedAxisBase = {
        ...(baseExplore.axis ?? {}),
        ...(overrideExplore.axis ?? {}),
    };
    return {
        ...baseExplore,
        ...overrideExplore,
        axis: {
            ...mergedAxisBase,
            column: mergeAxisSettings(baseExplore.axis?.column, overrideExplore.axis?.column),
            row: mergeAxisSettings(baseExplore.axis?.row, overrideExplore.axis?.row),
        },
        filters: mergedFilters,
    };
}

const actions = {
    id(d, receiver){
        return d.id
    },primitives(d, receiver, obj){
        const store = getPrimitiveStore(d) || {}
        return new Proxy(store, obj.structure )
    },
    avatarImageUrl(d, receiver){
        return resolvePrimitiveImageUrl(receiver);
    },
    _primitives(d, receiver, obj){
        return getPrimitiveStore(d)
    },
    processingErrors(d, receiver, obj){
        return PrimitiveConfig.processingErrors( d )
    },
    proxySource(d, receiver, obj){
        /*if( !receiver?.referenceParameters?.proxy ){
            return undefined
        }*/
        return resolveProxySourcePrimitive(receiver, obj)
    },
    renderConfig(d, receiver, obj){
        const proxySource = receiver?.proxySource;
        if( proxySource ){
            return proxySource.renderConfig
        }
        const cp = receiver?.configParent
        if( cp?.renderConfig ){
            return cp.renderConfig
        }
        const descriptor = Object.getOwnPropertyDescriptor(d, "renderConfig");
        return descriptor ? descriptor.value : undefined
    },
    percentageProgress(d, receiver, obj){
        if( d.type === "actionrunner" ){
            const runnerProgress = d.processing?.run_runner?.progress
            if( runnerProgress && Number.isFinite(Number(runnerProgress.percentage)) ){
                return Math.max(0, Math.min(1, Number(runnerProgress.percentage)))
            }
        }
        const genericProgress = selectGenericProcessingProgress(d.processing)
        const genericPct = progressToPercentage(genericProgress)
        if( genericPct !== undefined ){
            return genericPct
        }
        const progress = receiver.progress
        if( progress?.percentage ){
            return progress?.percentage
        }
        if( progress ){
            if( !isNaN(progress)){
                return progress
            }
        }
    },
    updateSourceDataForPin(d, receiver, obj){
        return (pinName, data)=>{
            const pinInfo = receiver.metadata?.pins?.output?.[pinName]
            if( pinInfo){
                if( pinInfo.source.startsWith("param.")){
                    const param = pinInfo.source.replace(/^param\./, "referenceParameters.")
                    receiver.setField(param, data)
                }

            }
        }
    },
    searchFor(d, receiver, obj){
      if( d.type !== "search"){
        return undefined
      }
        const linkedItemId = Object.entries(receiver._parentPrimitives ?? {}).find(d=>d[1].find(d=>d.startsWith("primitives.search.")))?.[0]
        return obj.primitive(linkedItemId) 
    },
    isRunning(d, receiver, obj){
        switch(d.type){
            case "search":
                if( d.processing?.query?.status === "running" ){
                    return true
                }
                return false
            case "actionrunner":
                if( d.processing?.run_step?.status === "running" ){
                    return true
                }
                return false
            case "category":
                if( d.processing?.mark_categories?.status === "running" ){
                    return true
                }
                return false
        }
    },
    progressStats(d, receiver, obj){
        switch(d.type){
            case "search":
                const toFiniteNumber = (value)=>{
                    const parsed = Number(value)
                    return Number.isFinite(parsed) ? parsed : undefined
                }
                const children = receiver.primitives.config.allSearch.map(d=>d.progressStats)
                const baseProgress = d.processing?.query?.progress
                let stats
                if( baseProgress ){
                    stats = {...baseProgress}
                } else {
                    stats = {
                        totalCount: d.processing?.query?.totalCount ?? 0,
                        totalScanned: d.processing?.query?.totalScanned ?? d.processing?.query?.scanned ?? 0
                    }
                }
                const normalizedTotalCount = toFiniteNumber(stats.totalCount)
                if( normalizedTotalCount !== undefined ){
                    stats.totalCount = normalizedTotalCount
                }
                const normalizedTotalScanned = toFiniteNumber(stats.totalScanned ?? stats.scanned)
                if( normalizedTotalScanned !== undefined ){
                    stats.totalScanned = normalizedTotalScanned
                }
                const searchConfig = receiver.getConfig
                let target = searchConfig.count
                if( searchConfig.countPerTerm ){
                    const terms = searchConfig.terms ?? ""
                    const sites = searchConfig.sites ?? ""
                    const termCount = Math.max(1, (Array.isArray(terms) ? terms : terms.split(",")).length)
                    const siteCount = Math.max(1, (Array.isArray(sites) ? sites : sites.split(",")).length)
                    target = termCount * siteCount * target
                }
                if( stats.totalCount === undefined && stats.totalScanned === undefined && d.processing?.query?.message){
                    const m = d.processing?.query?.message.match(/Found (\d+) items/)
                    if( m ){
                        stats.totalCount = m[1]
                        stats.totalScanned = m[1]
                    }
                }
                const fullScan = d.queryCount
                if( fullScan ){
                    stats.totalCount = fullScan
                }
                stats.max = target
                let totalCount = Number(stats.totalCount ?? 0) || 0
                let totalScanned = Number(stats.totalScanned ?? 0) || 0
                for(const child of children ){
                    if(!child){
                        continue
                    }
                    const childTotalCount = toFiniteNumber(child.totalCount)
                    if( childTotalCount !== undefined ){
                        totalCount += childTotalCount
                    }
                    const childTotalScanned = toFiniteNumber(child.totalScanned ?? child.scanned)
                    if( childTotalScanned !== undefined ){
                        totalScanned += childTotalScanned
                    }
                }
                stats.totalCount = totalCount
                stats.totalScanned = totalScanned
                return stats
            }
    },
    progress(d, receiver, obj){
        switch(d.type){
            case "search":
                if( receiver.processing?.query?.collecting && receiver.processing?.collect?.status !== "complete"){
                    return receiver.processing?.query?.message
                }else{
                    const childrenCollecting = receiver.primitives.config.allSearch.find(d=>d.processing?.query?.collecting && d.processing?.collect?.status !== "complete")
                    if( childrenCollecting ){
                        return childrenCollecting.processing.query.message
                    }
                }
                const stats = receiver.progressStats
                return `Found ${stats.totalCount} items (scanned ${stats.totalScanned} / max ${stats.max})`
            case "query":
                if( d.processing?.query?.progress ){
                    return d.processing?.query?.progress
                }
                const list = receiver.primitives.origin.allSummary
                if( list && list.length > 0){
                    const stats = list.reduce((a,d)=>{
                        const p = d.processing?.rebuild_summary?.progress
                        if( p?.percentage !== undefined){
                            a.count++
                            a.total += p.percentage
                        }
                        return a
                    }, {count: 0, total: 0})                    
                    if( stats.count > 0 ){
                        return `${Math.floor(stats.total / stats.count * 100)}% complete`
                    }
                }
                return
            case "actionrunner":
                if( d.processing?.run_runner?.progress ){
                    const progress = d.processing.run_runner.progress
                    if( typeof progress === "string" ){
                        return progress
                    }
                    if( typeof progress?.message === "string" ){
                        return progress.message
                    }
                    return progress
                }
                break
            case "category":
                return d.processing?.mark_categories?.progress
            case "categorizer":
                return receiver.primitives.origin.allCategory[0]?.processing?.mark_categories?.progress
        }
        const genericProgress = selectGenericProcessingProgress(d.processing)
        const genericMessage = progressToMessage(genericProgress)
        if( genericMessage ){
            return genericMessage
        }
    },
    flowStatus(d, receiver, obj){
        const root = d.processing?.flow
        return {
            status: root ? (root.error === "child_error" ? "child_error" : (root.status === "rerun" ? "complete" : root.status)) : "not_executed",
            ...(receiver.flowErrors ?? {})
        }
    },
    getResultSources(d, receiver, obj){
        let out = [receiver]
        let changed = false
        do{
            changed = false
            out = uniquePrimitives( out.map(d=>{
                if( d.referenceId === 82 || d.type == "summary" || d.type === "query"){
                    changed = true
                    return [d.primitives.source.allItems,d.primitives.link.allItems].flat()
                }else{
                    if( d !== receiver){
                        return d
                    }
                }
            }).flat(Infinity))
        }while(changed)
        return out
    },
    setFlow(d, receiver, obj){
        return (status)=>{
            if( receiver.type === "search" ){
                const parent = receiver.configParent
                if( parent && parent.inFlow && !parent.flowElement && parent.configParent.flowElement){
                    // In a nested search object - need to re-run parent too
                    receiver.setField("processing.query.status", status)
                    if(status === "error" || status === "rerun"){
                        parent.setField("processing.flow.status", status)
                    }else if( status === "complete"){
                        const allNested = parent.primitives.origin.allItems
                        if( allNested.every(d=>d.processing?.query?.status === "complete")){
                            console.log(`All children now complete - setting parent`)
                            parent.setField("processing.flow.status", status)
                        }
                    }else{
                        console.warn(`Unhandled setFlow ${status} on search`)
                    }
                }else{
                    receiver.setField("processing.flow.status", status)
                    receiver.setField("processing.query.status", status)
                }
                return
            }
            receiver.setField("processing.flow.status",status)
            if( status === "rerun" || status === "complete"){
                receiver.setField("processing.flow.error",null)
            }

        }
    },
    flowErrors(d, receiver, obj){
        const root = d.processing?.flow
        if( !root){
            return undefined
        }
        const error = {error: false}
        if( root.error ){
            error.error = root.error
            error.severity = root.status
            error.errorMessage = root.error
        }
        const childEntries = Object.entries(root.child ?? {}).filter(Boolean)

        if(  childEntries.length === 0){
            if( !error.error){
                return undefined
            }
        }

        error.childErrors = childEntries.map(([k,v])=>{
            return {
                primitive: obj.primitive(k),
                error: v.error
            }
        })
        
        return error
    },
    plainId(d, receiver, obj){
        return d.plainId
    },
    originAtLevel(d,receiver, obj){
        return function(level){
            let node = receiver
            while(level--){
                if( node ){
                    node = node.origin
                }
            }
            return node
        }                    
    },
    sourcePrimitivesForFlowInstance(d,receiver,obj){
        return (flowInstance)=>{
            const flowInstanceId = flowInstance.id
            return actions.int_sourcePrimitives(d, receiver, obj)([flowInstanceId])

        }
    },
    sourcePrimitives(d,receiver,obj){
        return actions.int_sourcePrimitives(d, receiver, obj)()
    },
    instanceStatus(d,receiver,obj){
        if( receiver.type !== "flowinstance"){
            return
        }
        return (async () => {
            const steps = receiver.origin.primitives.origin.allUniqueItems.filter(d=>d.type !=="flowinstance")
            const allInstanceSteps = [...receiver.primitives.origin.allItems, ...receiver.primitives.subfi.allItems];
            const stepsForInstance = steps.flatMap(step=>allInstanceSteps.filter(d=>d.configParent?.id === step.id))


            // add interim segments
            stepsForInstance.forEach(d=>{
                if( d.type === "flowinstance" ){
                    const segmentParents = d.primitives.imports.allItems.filter(d=>d.type == "segment")
                    for(const segmentParent of segmentParents){
                        const segmentImportIds = segmentParent.primitives.imports.allIds
                        for( const importId of segmentImportIds){
                            if( stepsForInstance.find(d=>d.id === importId)){
                                stepsForInstance.push(segmentParent)
                            }
                        }
                    }
                }
            })

            const subFlows = receiver.origin.primitives.origin.allFlow
            const subFlowsToScaffold = subFlows//.filter(d=>stepsForInstance.find(d2=>d2.originId === d.id) === undefined)

            const mainstore = MainStore()
            const itemCache = {}
            const countItemsForSource = (primitive, sourceId)=>{
                try{
                    if( typeof primitive.itemsForProcessingWithOptions !== "function"){
                        return 0
                    }
                    const items = primitive.itemsForProcessingWithOptions(sourceId, {cache: itemCache}) ?? []
                    if( Array.isArray(items)){
                        return items.length
                    }
                    if( Array.isArray(items?.data)){
                        return items.data.length
                    }
                    return 0
                }catch(err){
                    return 0
                }
            }
        
            const status = await PrimitiveConfig.buildFlowInstanceStatus(
              receiver,
              stepsForInstance,
              {
                fetchPrimitives: (ids)=>ids.map(d=>mainstore.primitive(d)),
                getPrimitives: (p)=>p.primitives,
                countItemsForSource: (stepPrimitive, sourceId)=>countItemsForSource(stepPrimitive, sourceId)
              },
              {
                withPrimitives: true,
                subFlowsToScaffold,
                inputDataCache: itemCache
              }
            );

        
            return status;
          })();
    },
    int_sourcePrimitives(d,receiver,obj){
        return (flowInstanceIds)=>{
            let out = []
            let outputPin
            if( receiver.type == "element"){
                const parentPage = receiver.origin
                const relName = (receiver._parentPrimitives[parentPage.id] ?? []).find(d=>d.startsWith("primitives.outputs."))
                if( relName ){
                    const [pinName, _] = relName.split(".").at(-1).split("_")
                    const pageInputs = parentPage.primitives.inputs
                    const importItem = Object.keys(pageInputs ?? {}).find(d=>d.endsWith(`_` + pinName))
                    outputPin = importItem.split("_")[0]
                    if( importItem ){
                        const containers = pageInputs[importItem].allItems
                        containers.forEach(s=>{
                            out.push(s)
                            if(s.flowElement){
                                out.push(...s.primitives.config.allItems)
                            }
                        })
                    }
                }
            }
            if( flowInstanceIds ){
                out = out.filter(d=>flowInstanceIds.includes(d.origin.id))
            }
            if( outputPin ){
                return out.flatMap(d=>d.outputs[outputPin])
            }
            return []
        }
    },
    doesImport(d, receiver, obj){
        return (id, filters)=>{
            //console.log(`Check primitive `, id, receiver.id)
            return PrimitiveConfig.checkImports( receiver, id, filters)
        }
    },
    filterItems(d, receiver, obj){
        return (list, filters)=>{
            let thisSet = undefined
            for(const filter of filters ){
                let runtimeFilter = filter
                const runtimeRelationships = [filter?.relationship].flat().filter(Boolean)
                if( filter?.type === "parent" && runtimeRelationships.includes("batch.latest") ){
                    const sinkId = filter.sourcePrimId ?? [filter.value].flat().find((value)=>typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value))
                    if( sinkId ){
                        const sinkPrimitive = obj.primitive(sinkId)
                        const batchId = sinkPrimitive?.processing?.stream?.lastTriggeredBatchId ?? "__none__"
                        const nextRelationships = runtimeRelationships.map((entry)=>entry === "batch.latest" ? `batch.${batchId}` : entry)
                        runtimeFilter = {
                            ...filter,
                            relationship: nextRelationships.length === 1 ? nextRelationships[0] : nextRelationships
                        }
                    }
                }
                
                let {resolvedFilterType, pivot, relationship, check, includeNulls, skip, isRange} = PrimitiveConfig.commonFilterSetup( runtimeFilter )
                if( skip){
                    continue
                }
                const setToCheck = (thisSet || list)

                window.mytimer = window.mytimer  ?? 0
                const p1 = performance.now()
                let lookups = setToCheck.map(d=>pivot === 0 ? [d] : d.relationshipAtLevel(relationship, pivot))
                if( pivot === 1){

                    window.mytimer += performance.now() - p1
                }

                let scope
                if( runtimeFilter.type !== "segment_filter"){
                    if( runtimeFilter.type === "parent"){
                        if( runtimeFilter.sourcePrimId ){
                            let filterSourcePrimitive = obj.primitive(runtimeFilter.sourcePrimId)
                            const isMongoId = (str) => /^[a-fA-F0-9]{24}$/.test(str);
                            if( filterSourcePrimitive ){

                                if( runtimeFilter.value && Array.isArray(runtimeFilter.value) && runtimeFilter.value.find(d=>!isMongoId(d))){
                                    if( filterSourcePrimitive.referenceId === PrimitiveConfig.Constants.EVAL_CATEGORIZER){
                                        const relevantInstance = filterSourcePrimitive.primitives.config.allItems.find(d=>d.parentPrimitiveIds.includes(receiver.originId))
                                        if( relevantInstance ){
                                            const embedded = relevantInstance.primitives.origin.allCategory[0]
                                            if( embedded ){
                                                const children = embedded.primitives.allItems
                                                scope = embedded.primitives.allIds
                                                check = check.map(d=>children.find(d2=>d2.title === d)?.id ?? d)
                                            }
                                            
                                        }
                                    }
                                }else{
                                    scope = filterSourcePrimitive?.primitives.allIds ?? []
                                }
                            }
                        }
                    }else if( runtimeFilter.subtype === "question"){
                        const prompts = uniquePrimitives(setToCheck.map(d=>d.findParentPrimitives({type: "prompt"})).flat()),
                        scope = prompts.map(d=>d.id)
                        check = check.map(d=>prompts.filter(d2=>d2.parentPrimitiveIds.includes(d))).flat().map(d=>d.id)
                    }else if( runtimeFilter.subtype === "search"){
                        if( includeNulls){
                            scope = uniquePrimitives(setToCheck.map(d=>d.findParentPrimitives({type: "search"})).flat()).map(d=>d.id)
                        }
                    }else if(runtimeFilter.type === "not_category_level1"){
                        includeNulls = true
                        check = []
                        if( runtimeFilter.sourcePrimId ){
                            scope = obj.primitive(runtimeFilter.sourcePrimId).primitives.allIds
                        }
                    }
                }
                    
                
                thisSet = PrimitiveConfig.doFilter( {resolvedFilterType, filter: runtimeFilter, setToCheck, lookups, check, scope, includeNulls, isRange}, {findFilterMatches: (a,v)=>findFilterMatches(a,v), parentIds:(primitive)=>primitive.parentPrimitiveIds})
            }
            return thisSet || list
        }                        
    },
    fetchItemsForAction(d, receiver, obj){
        return (options = {})=>{

            let list = []
            let primitive = receiver
            let source = options.source ?? primitive
            
            
            let type = primitive.referenceParameters?.type 
            const config = primitive.getConfig ?? {}
            const target = config.target || "children"
            const referenceId = config.referenceId 
            const constrainIds = primitive.primitives.params?.constrain_parent_chain.allIds

            console.log(constrainIds)
            
            if(target === "descend"){
                list = source.primitives.strictDescendants
            }else if(target === "all_descend"){
                list = source.primitives.descendants
            }else if(target === "ref"){
                list = source.primitives.ref.uniqueAllItems
            }else if(target === "link"){
                list = source.primitives.link.uniqueAllItems
            }else if(target === "children"){
                list = source.primitives.origin.uniqueAllItems
            }else if(target === "items"){
                list = source.itemsForProcessing
            }else if(target === "items_children"){
                list = source.itemsForProcessingWithOptions(undefined, {forceImports: true})
            }else if( target === "hierarchy"){
                list = source.findParentPrimitives({referenceId: referenceId ? [referenceId] : undefined, type: type ? [type] : undefined, first: true})
            }
            
            if( type ){
                list = list.filter((d)=>d.type === type)
            }
            if( referenceId ){
                if( Array.isArray(referenceId )){
                    list = list.filter((d)=>referenceId.includes(d.referenceId))
                }else{
                    list = list.filter((d)=>d.referenceId === referenceId)
                }
            }
            
            if( list === undefined){
                return []
            }
            if( constrainIds.length > 0){
                const validIds = obj.primitive(constrainIds[0]).itemsForProcessing.map(d=>d.id)
                
                list = list.filter(d=>validIds.includes(d.id))
            }
            
            return list
        }
    },
    itemsForProcessing(d, receiver, obj){
        return receiver.itemsForProcessingWithOptions()
    },
    itemsForProcessingWithParams(d, receiver, obj){
        return (p)=>{
            return receiver.itemsForProcessingWithOptions(undefined, {params: p})
        }
    },
    itemsForProcessingWithFilter(d, receiver, obj){
        return (f,o)=>{
            let items = receiver.itemsForProcessingWithOptions(undefined, o)
            if( f && f.length > 0){ 
                return receiver.filterItems( items, f)
            }else{
                return items
            }
        }
    },
    filterTargets(d, receiver, obj){
        if( receiver.referenceParameters?.importConfig ){
            const idsToLookup = []
            let relationships = []
            let doImport
            let importFrom = receiver
            let importFilter
            receiver.referenceParameters.importConfig.forEach(d2=>{
                return d2.filters?.forEach( d=>{
                    if( d.type === "parent"){
                        idsToLookup.push(d.value)
                    }else if( d.type === "title" || d.type === "parameter" || d.type === "structured_summary"){
                        doImport = true
                        if( d.relationship ){
                            relationships.push(d.relationship)
                        }
                        importFilter = (d3)=>d3.title === d.value
                        importFrom = obj.primitive( d2.id )
                    }
                })
            })
            if( doImport && (relationships.length > 0 || importFilter)){
                let items = importFrom.itemsForProcessing
                let mapped = []
                if( relationships.length > 0){

                    for(const rel of relationships){
                        mapped.push(items.flatMap(d=>d.relationshipAtLevel(rel ?? "origin", rel?.length ?? 1)))
                    }
                    mapped = uniquePrimitives(mapped.flat(Infinity))
                }else{
                    mapped = items
                }
                if( importFilter ){
                    mapped = mapped.filter(importFilter)
                }                                
                return mapped
            }
            if( idsToLookup.length > 0){
                return uniquePrimitives(idsToLookup.flatMap(d=>obj.primitive(d)?.filterTargets))
            }
            return []
        }else{
            if( receiver.type === "summary" ){
                const segmentParents = receiver.parentPrimitives.filter(d=>d.type === "segment")
                if( segmentParents ){
                    return segmentParents.flatMap(d=>d.filterTargets)
                }
            }
        }
        return [receiver]
    },
    filterDescription(d, receiver, obj){
       return receiver.filterTargets.map(d=>d.title).join(", ")
    },
    itemsForProcessingFromImport(d, receiver, obj){
        return (p, o)=>receiver.itemsForProcessingWithOptions(p.id, o)
    },
    itemsForProcessingWithOptions(d, receiver, obj){
        return (id, options = {})=>{
            const bypassProxy = options?.bypassProxy === true
            if( !bypassProxy ){
                const proxySource = resolveProxySourcePrimitive(receiver, obj);
                if( proxySource ){
                    return proxySource.itemsForProcessingWithOptions(id, options)
                }
            }
            if( !options.cache ){
                options.cache = {}
            }
            const excludedTypes = new Set(["segment", "category", "query", "report", "reportinstance"]);
            const configParams = options.params ?? receiver.getConfig ?? {}
            const rawLimit = configParams?.limit
            const numericLimit = rawLimit === "" ? undefined : (typeof rawLimit === "number" ? rawLimit : Number(rawLimit))
            const normalizedLimit = Number.isFinite(numericLimit) && numericLimit > 0 ? Math.floor(numericLimit) : undefined
            const shouldLimit = normalizedLimit !== undefined && (receiver.type === "view" || receiver.type === "query")
            const useRandomLimit = shouldLimit && !!configParams?.limitRandom
            const applyConfiguredLimit = (items)=>{

                                if( receiver.plainId === 1541955){
                                    const tl = []
                                    const seen = new Set()
                                    for( const tp of items){
                                        const url = tp.referenceParameters?.url
                                        if( url ){
                                            if( !seen.has(url)){
                                                seen.add( url )
                                                tl.push( tp )
                                            }
                                        }
                                    }
                                    items = tl
                                }


                if( !shouldLimit || !Array.isArray(items)){
                    return items
                }
                if( items.length <= normalizedLimit ){
                    return items
                }
                if( useRandomLimit ){
                    return pickAtRandom(items, normalizedLimit)
                }
                return items.slice(0, normalizedLimit)
            }

            const applyAxisPruning = (list) => {
                if( receiver.type === "view" || receiver.type === "query" ){
                    const axisConfig = receiver.getConfig?.explore?.axis ?? {}
                    if( CollectionUtils.axisNeedsPruning(axisConfig?.column) || CollectionUtils.axisNeedsPruning(axisConfig?.row) ){
                        const columnAxis = CollectionUtils.primitiveAxis(receiver, "column", list)
                        const rowAxis = CollectionUtils.primitiveAxis(receiver, "row", list)
                        const viewFilterAxes = (receiver.getConfig?.explore?.filters ?? []).map((_, idx)=>CollectionUtils.primitiveAxis(receiver, idx, list))
                        const axisPrep = CollectionUtils.prepareAxisFilteredData(list, {
                            columns: columnAxis,
                            rows: rowAxis,
                            viewFilters: viewFilterAxes,
                            hideNull: receiver.getConfig?.explore?.hideNull,
                            alreadyFiltered: true,
                            cacheId: receiver.id
                        })
                        if( Array.isArray(axisPrep?.filteredItems) ){
                            return axisPrep.filteredItems
                        }
                    }
                }
                return list
            }

            const forceImportsFoRelay = receiver.referenceParameters?.relayImports
            if( Object.keys(receiver.primitives).includes("imports") && (options.forceImports || (receiver.type !== "query" && receiver.type !== "summary" && receiver.type !== "search" && receiver.type !== "flowcontrol"))){
                let fullList = []
                let loops = 0 
                const matchesReferenceId = (item)=>{
                    if(!configParams.referenceId){
                        return false
                    }
                    const referenceTargets = Array.isArray(configParams.referenceId) ? configParams.referenceId : [configParams.referenceId]
                    return referenceTargets.includes(item.referenceId)
                }
                for( const source of receiver.primitives.imports.allItems){
                    if( id && source.id !== id){
                        continue
                    }
                    if( configParams.descend === false && matchesReferenceId(source)){
                        fullList = fullList.concat(source)
                        continue
                    }
                    let list = []
                    if( source.type === "flow" && !receiver.inFlow){
                        const instances = source.primitives.origin.allUniqueFlowInstance
                        const address = source.primitives.outputs.paths(receiver.id)[0]
                        if( address ){
                            const [outputPin, inputPin] = address.slice(1).split("_")
                            return applyConfiguredLimit(applyAxisPruning(uniquePrimitives(instances.flatMap(d=>d.outputs[outputPin]?.data))))
                        }
                        return applyConfiguredLimit(applyAxisPruning(uniquePrimitives(instances.flatMap(d=>d.outputs.output?.data))))
                    }else if( source.inFlow && !receiver.inFlow){
                        const address = source.primitives.outputs.paths(receiver.id)[0]
                        if( address ){
                            const [outputPin, inputPin] = address.slice(1).split("_")
                            return applyConfiguredLimit(source.outputs[outputPin]?.data ?? [])
                        }
                        //return source.itemsForProcessing
                    }else{
                        const addresses = source.primitives.outputs.paths(receiver.id)
                        const inThisFlow = receiver.originId === source.id
                        let done = false
                        for(const address of addresses){
                            if (inThisFlow) {
                                if (address.startsWith('.impout_')) {
                                    continue
                                }
                            }
                            if( address && address !== '.impout_impin' && address !== '.impin_impin'){
                                const [outputPin, inputPin] = address.slice(1).split("_")
                                if( source.type === "flowinstance" && receiver.origin.id === source.id ){
                                    const relayPin = typeof outputPin === "string" && outputPin.startsWith("relay-")
                                    if( relayPin ){
                                        const upstreamOutputPin = outputPin.slice("relay-".length)
                                        const validFlowIds = new Set(
                                            (source.origin?.primitives?.validflow?.allIds ?? []).map((flowId)=>`${flowId}`)
                                        )
                                        const seenRelayPrimitiveIds = new Set()
                                        const upstreamInstances = source.primitives?.upstream?.allItems ?? []
                                        for(const upstreamInstance of upstreamInstances){
                                            if( upstreamInstance?.type !== "flowinstance" ){
                                                continue
                                            }
                                            if( validFlowIds.size > 0 ){
                                                const upstreamFlowId = upstreamInstance?.origin?.id ? `${upstreamInstance.origin.id}` : undefined
                                                if( !upstreamFlowId || !validFlowIds.has(upstreamFlowId) ){
                                                    continue
                                                }
                                            }
                                            const relayItems = upstreamInstance.outputs?.[upstreamOutputPin]?.data ?? []
                                            for(const relayItem of relayItems){
                                                const relayItemId = relayItem?.id ? `${relayItem.id}` : undefined
                                                if( relayItemId ){
                                                    if( seenRelayPrimitiveIds.has(relayItemId) ){
                                                        continue
                                                    }
                                                    seenRelayPrimitiveIds.add(relayItemId)
                                                }
                                                fullList.push(relayItem)
                                            }
                                        }
                                    }else{
                                        fullList = fullList.concat(source.outputs?.[outputPin]?.data ?? [])
                                    }
                                    done = true
                                }else{
                                    fullList = fullList.concat( source.outputs[outputPin]?.data ?? [] )
                                    done = true
                                }
                            }
                        }
                        if( done ){
                            continue
                        }

                    }
                    // For question sources, forward evidence (children of prompts) not prompts themselves
                    if (source.type === "question") {
                        const promptChildren = source.primitives.origin.uniqueAllItems; // prompts
                        list = uniquePrimitives(
                            promptChildren.flatMap(p => p.primitives.auto.uniqueAllItems) // evidence
                        );
                        fullList = fullList.concat(list);
                        continue;
                    }
                    let node = source.primitives
                    if( Object.keys(node).includes("imports")  ){
                        const test = options.cache[source.id]
                        if( test ){
                            list = test
                        }else{
                            list = list.concat(source.itemsForProcessingWithOptions(undefined, {forceImports: forceImportsFoRelay, cache:options.cache}) )
                            options.cache[source.id] = list
                        }
                    }else{
                        if( receiver.referenceParameters?.path ){
                            node = node.fromPath(receiver.referenceParameters?.path)
                        }
                        if( !receiver.referenceParameters?.path && source.type === "segment"){
                            list = source.nestedItems
                        }else{
                            if( source.type === "action" && source.getConfig.local){
                                list = [source]
                            }else{

                                list = uniquePrimitives(Object.keys(node).filter(d=>d !== "inputs" && d !== "outputs" && d !== "chat").flatMap(d=>node[d].allItems))
                            }
                            //list = node.uniqueAllItems
                        }
                    }
                    const params = configParams
                    if( params.descend ){
                        if( params.referenceId ){
                            const match = params.referenceId;
                            const matchSet = new Set(Array.isArray(match) ? match : [match]);
                            const expanded = new Set()
                            const existingSet = new Set()
                            for(const d of list){
                                existingSet.add(d.id)
                            }

                            const newList = [];
                            for (const d of list) {
                                if( !matchSet.has(d.referenceId) ){
                                    let depIds
                                    if( params.descendRel){
                                        depIds = params.descendRel.flatMap(rel=>d.primitives[rel].allIds)
                                    }else{
                                        depIds = d.primitives.strictDescendantIds
                                    }
                                    for(const v of depIds){
                                        if( !existingSet.has( v )){
                                            expanded.add( v )
                                        }
                                    }
                                }else{
                                    newList.push(d);
                                }
                            }

                            list = newList//.filter(Boolean);
                            for (const d of expanded) {
                                const p = obj.primitive(d)
                                if( p && matchSet.has(p.referenceId) ){
                                    list.push(p);
                                }
                              }
                        }else{
                            list = list.flatMap(d=>[d,d.primitives.strictDescendants]).flat().filter(d=>d)
                            list = uniquePrimitives(list)
                        }
                    }else if( params.referenceId ){
                        const match = params.referenceId
                        if( Array.isArray(match)){
                            list = list.filter(d=>match.includes(d.referenceId))
                        }else{
                            list = list.filter(d=>d.referenceId === match) 
                        }
                    }
                    if( params.type ){
                        list = list.filter(d=>d.type === params.type) 
                    }
                    if( receiver.type === "actionrunner"){
                        list = list.filter(d=>d.type == "entity" || d.type == "result" || d.type == "evidence") 
                    }
                    let config
                    config = receiver.referenceParameters?.importConfig?.filter(d=>d.id === source.id)
                    if( config && config.length > 0){
                        let filterOut
                        for(const set of config ){
                            if( set.filters ){
                                filterOut ||= []
                                let thisSet = receiver.filterItems(list, set.filters)
                                if( thisSet ){
                                    filterOut = filterOut.concat( thisSet )
                                }
                            }
                        }
                        list = filterOut ?? list
                        for(const set of config ){
                            if( set.referenceId ){
                                list = uniquePrimitives( list.flatMap(d=>d.primitives.filter(d=>d.referenceId === set.referenceId)))
                            }
                        }
                    }
                    if( params?.pivot){
                        if( typeof(params?.pivot) === "number"){
                            list = uniquePrimitives(list.map(d=>d.originAtLevel(params?.pivot)).flat())
                        }else{
                            list = uniquePrimitives(list.flatMap(d=>d.relationshipAtLevel(params.pivot, params.pivot.length)))
                        }
                    }
                    fullList = fullList.concat(list) 
                    loops++
                }
                if(receiver.type === "view"  && !options.ignoreFinalViewFilter){
                    let viewFilters = CollectionUtils.convertCollectionFiltersToImportFilters( receiver )
                    if( viewFilters.length === 0 && receiver.inFlow){
                        if( receiver.configParent  ){
                            viewFilters = CollectionUtils.convertCollectionFiltersToImportFilters( receiver.configParent ).map(d=>{
                                if( d.type == "parent" && d.sourcePrimId){
                                    if( receiver.origin?.type === "flowinstance"){
                                        const sourcePrim = obj.primitive(d.sourcePrimId)
                                        const instanceSource = sourcePrim.primitives.config.allItems.find(d=>d.originId == receiver.originId)
                                        if( instanceSource ){
                                            let useId = instanceSource.id 
                                            if( instanceSource.type === "categorizer"){
                                                useId = instanceSource.primitives.origin.allIds[0] 
                                            }
                                            if( useId ){
                                                return {
                                                    ...d,
                                                    sourcePrimId: useId
                                                }
                                            }
                                        }
                                    }
                                }
                                return d
                            })
                        }
                    }
                    if( loops > 1){
                        fullList = uniquePrimitives(fullList)
                        loops = 1
                    }
                    fullList = receiver.filterItems(fullList, viewFilters)
                }
                let finalList = loops > 1 ? uniquePrimitives(fullList) : fullList
                return applyConfiguredLimit(applyAxisPruning(finalList))
            }else if(receiver.type === "flowcontrol"){
                const activeStates = receiver.processing?.flowControl?.matchedStates ?? []
                if( activeStates.length > 0){
                    const inputData = receiver.inputs
                    const allStates = receiver.getConfig.flowControl?.states
                    const stateInfo = activeStates.map(d=>allStates.find(d2=>d2.id === d))
                    const allPins = Array.from(new Set(stateInfo.flatMap(d=>d?.passThroughPins ?? [])))
                    const mappedData = allPins.flatMap(d=>{
                        const sourceData = inputData[d]
                        if( sourceData?.config === "primitive"){
                            return sourceData?.data ?? []
                        }
                        return []
                    })
                    return applyConfiguredLimit(uniquePrimitives( mappedData ))
                }
                return applyConfiguredLimit([])
            }else if(receiver.type === "flowinstance"){
                return applyConfiguredLimit([])
            }else if(receiver.type === "summary"){
                return applyConfiguredLimit([receiver])
            }else{
                let ids = Object.keys(receiver.primitives).filter(d=>d !== "imports" && d !== "chat" && d !== "params" && d !=="config" && d !=="inputs" && d !=="outputs" ).map(d=>receiver.primitives[d].allIds).flat()
                let list = []
                const check = new Set()
                for( const d of ids ){
                    if( !check.has(d)){
                        check.add(d)
                        const p = obj.primitive(d)
                        if( p ){
                            list.push(p)
                        }
                    }
                }
                const params = configParams
                if( receiver.type === "actionrunner" || receiver.type === "action"){
                    if( params.local ){
                        list = [receiver]
                    }else{
                        list = list.filter(d=>d.type == "entity" || d.type == "result" || d.type == "evidence") 
                    }
                }else if( receiver.type === "query" || receiver.type  === "segment" || receiver.type === "search"){
                    if( receiver.type === "query" && !options.ignoreFinalViewFilter){
                        const viewFilters = CollectionUtils.convertCollectionFiltersToImportFilters( receiver )
                        list = receiver.filterItems(list, viewFilters)
                    }
                    if( receiver.type === "search"){
                        const nestedSearch = [receiver, ...receiver.primitives.origin.allSearch].filter(d=>d)
                        list = nestedSearch.flatMap(d=>d.primitives.allUniqueResult)                        
                    }
                    if( params.extract ){
                        const check = [params.extract].flat()
                        list = list.filter(d=>check.includes(d.referenceId))
                    }
                    list = list.filter(d => !excludedTypes.has(d.type));
                }
                return applyConfiguredLimit(list)
            }                        
        }
    },
    referenceParameters(d, receiver, obj){
        /*if( d.type === "assessment"){
            d._referenceParameters ||= {}
            d._referenceParameters.levels = d._referenceParameters.levels || {}
        }*/
        return d.referenceParameters
    },
    origin(d, receiver, obj){
        if( d._origin){
            return d._origin 
        }
        //let origin = receiver.parentPrimitiveRelationships["origin"]
        let originId = receiver.originId
        if( originId ){
            const origin = obj.primitive(originId)
            d._origin = origin
            return origin
        }
    },
    originId(d, receiver, obj){
        let id = undefined;
        if (receiver._parentPrimitives) {
            for (const key in receiver._parentPrimitives) {
                if (receiver._parentPrimitives[key].includes("primitives.origin")) {
                    id = key;
                    break;
                }
            }
        }
        return id
    },
    outputs(d, receiver, obj){
        if( receiver.type === "flow" || receiver.type === "flowinstance"){
            return receiver.fetchInputs(undefined, "outputs" , "output")
        }
        let outputMap = PrimitiveConfig.getOutputMap(receiver)
        const out = {}
        const c = {}
        const lookupCache = {}
        for(const d of outputMap){
            let targetMap = c[d.targetId]
            if( d.outputPin !== "impout" && d.targetPin === "impin"){
                const query = receiver.metadata?.pins?.output?.[d.outputPin]?.query
                if( query ){
                    out[d.outputPin] = {data: receiver.follow(receiver.metadata?.queries?.[query]?.steps ?? [], lookupCache)}
                }else if( receiver.type === "flowcontrol" ){
                    const activeStates = receiver.processing?.flowControl?.matchedStates ?? []
                    const stateForPin = receiver.getConfig?.flowControl?.states?.find(d2=>d2.pinKey === d.outputPin)
                    if( activeStates.includes(stateForPin?.id)){
                        out[d.outputPin] = {data: receiver.itemsForProcessing, config:"primitive"}
                    }
                }else if( receiver.type === "question" ){
                    // Question outputs its evidence (via itemsForProcessing which is already patched)
                    out[d.outputPin] = {data: receiver.itemsForProcessing, config:"primitive"}
                }else{
                    console.error(`Cant handle pin connected to import without query`, d)
                }
            }else{
                if(!targetMap){
                    const target = obj.primitive( d.targetId )
                    targetMap = target.fetchInputs( receiver.id)
                    c[d.targetId] = targetMap
                }
                out[d.outputPin] = targetMap[d.targetPin]
            }
        }
        return out
    },
    inFlow(d, receiver, obj){
        if( receiver.flowElement){
            return true
        }
        const configParent = receiver.parentPrimitiveWithRelationship("config")?.[0]
        if( configParent?.flowElement ){
            return true
        }
        return false
    },
    inputPinsWithStatus(d, receiver, obj){
        const pins = receiver.inputPins
        return Object.keys(pins).reduce((a,pinName)=>{
            a[pinName] = {
                ...pins[pinName],
                connected: pinName === "impin" ? receiver.primitives.imports.allIds.length > 0 : Object.keys(receiver.primitives.inputs).some(d=>d.endsWith(`_${pinName}`))
            }
            return a
        },{})
    },
    outputPinsWithStatus(d, receiver, obj){
        const pins = receiver.outputPins
        return Object.keys(pins).reduce((a,pinName)=>{
            a[pinName] = {
                ...pins[pinName],
                connected: pinName === "impout" ? Object.values(receiver._parentPrimitives ?? {}).some(d=>d.some(d=>d === "primitives.imports")) : Object.values(receiver._parentPrimitives).some(d=>d.some(d=>d.startsWith(`primitives.inputs.${pinName}_`)))
            }
            return a
        },{})
    },
    inputPins(d, receiver, obj){
        let dynamicPinSource = receiver
        let generatorPins = {}
        if(receiver.type === "flowinstance"){
            dynamicPinSource = receiver.origin
        }else if( !receiver.flowElement ){
            dynamicPinSource = receiver.configParent ?? receiver
        }
        if( receiver.type === "actionrunner"){
            const rConfig  = dynamicPinSource.getConfigWithoutOverrides()                                
            if( rConfig.generator){
                const generateTarget = obj.category( rConfig.generator)
                generatorPins = generateTarget?.ai?.generate?.inputs ?? {}
            }else{
                const targetCategory = obj.category( rConfig.referenceId)
                generatorPins = PrimitiveConfig.getPinsForAction( targetCategory, rConfig.action)
            }
        }
        return {
            ...receiver._pins("input"),
            ...PrimitiveConfig.getDynamicPins(dynamicPinSource, dynamicPinSource.getConfigWithoutOverrides(), "inputs"),
            ...generatorPins
        }
    },
    outputPins(d, receiver, obj){
        const dynamicPinSource = receiver.type === "flowinstance" ? receiver.origin : receiver
        return {
            ...receiver._pins("output"),
            ...PrimitiveConfig.getDynamicPins(dynamicPinSource, dynamicPinSource.getConfigWithoutOverrides(), "outputs")
        }

    },
    _pins(d, receiver, obj){
        return (mode = "input")=>{
            const base = {
                ...(receiver.metadata?.pins?.[mode] ?? {})
            }
            if( receiver.type === "query" || receiver.type === "view"){
                if( mode === "output"){
                    //base["rowAxis"] =  {name: "Axis (row)", axis: true, types: ["primitive", "string"]}
                    //base["colAxis"] =  {name: "Axis (col)", axis: true, types: ["primitive", "string"]}
                }else{
                    //base["rowAxis"] =  {name: "Axis (row)", axis: true, types: ["primitive","string"]}
                    //base["colAxis"] =  {name: "Axis (col)", axis: true, types: ["primitive", "string"]}
                    base["rowAxisFilter"] =  {name: "Axis filter (row)", axis: true, types: ["primitive","string"]}
                    base["colAxisFilter"] =  {name: "Axis filter (col)", axis: true, types: ["primitive", "string"]}
                }
            }
            if( mode === "output" && receiver.type === "flow"){
                return base
            }
            return {
                [mode === "input" ? "impin" : "impout"]:{
                    name: `Imports ${mode}`,
                    types: ["primitive"]
                },
                ...base
            }
        }
    },
    fetchInputs(d, receiver, obj){
        return (sourceId, mode = "inputs", pinMode = "input")=>{
            let inputMap = PrimitiveConfig.getInputMap(receiver, mode)
            if( mode === "outputs"){
                if( receiver.inFlow ){
                    const cp = receiver.configParent
                    const outputs = cp ? cp.primitives.outputs : receiver.primitives.output;
                    const sectionMap = new Map();
                    Object.entries(outputs).forEach(([sectionKey, sectionValue], sIdx) => {
                        const posMap = new Map();
                        sectionValue.allItems.forEach((item, groupIdx) => {
                            const ids = item.primitives.config.allIds;
                            for (const id of ids) {
                                if (!posMap.has(id)) posMap.set(id, groupIdx);
                            }
                        });
                        sectionMap.set(sectionKey, { sIdx, posMap });
                    });
                    
                    inputMap.forEach(entry => {
                        const key = `${entry.sourcePin}_${entry.inputPin}`;
                        const info = sectionMap.get(key);
                        if (info) {
                            entry._sIdx = info.sIdx;
                            entry._idx  = info.posMap.get(entry.sourceId) ?? -1;
                        } else {
                            entry._sIdx = Infinity;
                            entry._idx  = -1;
                        }
                    });
                    
                    inputMap.sort((a, b) =>
                        a._sIdx - b._sIdx || a._idx - b._idx
                    );
                }
            }
            if( sourceId ){
                inputMap = inputMap.filter(d=>d.sourceId === sourceId)
            }
            
            inputMap = inputMap.map(d=>{
                let sourcePrimitive = obj.primitive(d.sourceId)
                if( !sourcePrimitive ){
                    return undefined
                }
                let sourcePinConfig = sourcePrimitive.metadata?.pins?.output?.[d.sourcePin]

                if( sourcePrimitive.type === "flow" || sourcePrimitive.type === "flowinstance"){
                    const flow = sourcePrimitive.type === "flow" ? sourcePrimitive : sourcePrimitive.findParentPrimitives({type: ["flow"]})[0]
                    if( !flow ){
                        return undefined
                    }
                    if( d.sourcePin === "siblings" ){
                        let passThroughData
                        if( sourcePrimitive.type === "flowinstance" ){
                            const parentFlowInstance = sourcePrimitive.findParentPrimitives({type: "flowinstance",first: true})[0]
                            if( parentFlowInstance ){
                                const relevantSubFIs = parentFlowInstance.primitives.subfi.allItems
                                const peers = relevantSubFIs.filter(d=>d.id !== sourcePrimitive.id)
                                passThroughData = peers.map(d=>d.title).join(", ")
                            }
                        }
                        sourcePinConfig = {
                            types: ["string"],
                            passThroughData, 
                            source: `param.${d.sourcePin}`
                        }                                        
                    }

                    if( flow.referenceParameters?.controlPins?.[d.sourcePin]){
                        sourcePrimitive = flow
                        sourcePinConfig = {
                            ...flow.referenceParameters?.controlPins?.[d.sourcePin],
                            source: `param.${d.sourcePin}`
                        }                                        
                    }else if( flow.referenceParameters?.inputPins?.[d.sourcePin]){
                        sourcePrimitive = sourcePrimitive
                        sourcePinConfig = {
                            ...flow.referenceParameters?.inputPins?.[d.sourcePin],
                            source: `param.${d.sourcePin}`
                        }                                        
                    }else if( flow.referenceParameters?.outputPins?.[d.sourcePin]){
                        sourcePrimitive = sourcePrimitive
                        sourcePinConfig = {
                            ...flow.referenceParameters?.outputPins?.[d.sourcePin],
                            source: `param.${d.sourcePin}`
                        }                                        
                    }
                }
                const inputMapSource = receiver.type === "flowinstance" ? receiver.origin : receiver
                if( !inputMapSource ){
                    return undefined
                }
                //let inputMapConfig = inputMapSource.metadata?.pins?.[pinMode]?.[d.inputPin]

                //let inputMapConfig = receiver._pins(pinMode)[d.inputPin]
                let inputMapConfig = inputMapSource._pins?.(pinMode)?.[d.inputPin]
                
                if( inputMapConfig?.hasConfig ){
                    const localConfig = inputMapSource.getConfigWithoutOverrides().pins?.[d.inputPin] ?? {}
                    inputMapConfig = {
                        ...inputMapConfig,
                        ...localConfig
                    }
                }
                return {
                    ...d,
                    sourcePrimitive,
                    sourcePinConfig,
                    inputMapConfig
            }}).filter(Boolean)

            //const dynamicPinSource = receiver.type === "flowinstance" ? receiver.origin : receiver
            let dynamicPinSource = receiver
            if(receiver.type === "flowinstance"){
                dynamicPinSource = receiver.origin ?? receiver
            }else if( !receiver.flowElement ){
                dynamicPinSource = receiver.configParent ?? receiver
            }
            let dynamicPins = dynamicPinSource.type === "flowcontrol" || dynamicPinSource.type === "categorizer" || dynamicPinSource.type === "flow" || dynamicPinSource.type === "action" || dynamicPinSource.type === "query" || dynamicPinSource.type === "summary" || dynamicPinSource.type === "page" || dynamicPinSource.type === "search" ? PrimitiveConfig.getDynamicPins(dynamicPinSource, dynamicPinSource.getConfigWithoutOverrides(), "inputs") : {}
            

            if( (receiver.type === "flow" || receiver.type === "flowinstance") && mode === "outputs"){
                dynamicPins = {
                    ...dynamicPins,
                    ...PrimitiveConfig.getDynamicPins(dynamicPinSource, dynamicPinSource.getConfigWithoutOverrides(), "outputs")
                }
            }
            let generatorPins = {}
            if( receiver.type === "actionrunner"){
                const rConfig  = receiver.getConfigWithoutOverrides()                                
                if( rConfig.generator){
                    const generateTarget = obj.category( rConfig.generator)
                    generatorPins = generateTarget?.ai?.generate?.inputs ?? {}

                }else{
                    const targetCategory = obj.category( rConfig.referenceId)
                    generatorPins = PrimitiveConfig.getPinsForAction( targetCategory, rConfig.action)
                }
                dynamicPins = {
                    ...dynamicPins,
                    ...generatorPins
                }
            }

            const flowPeerIds = receiver?.primitives?.flow_peer?.allIds ?? []
            let interim = PrimitiveConfig.alignInputAndSource(inputMap,  dynamicPins, flowPeerIds)

            const peerFlowIdSet = new Set(flowPeerIds.map(id => (typeof id === "string" ? id : `${id}`)))
            const isPeerFlowInstance = (primitiveCandidate) =>
                primitiveCandidate?.type === "flowinstance" && peerFlowIdSet.has(primitiveCandidate.id)
            const transformHelpers = {
                getImports: (entry)=>{
                    return entry.sourcePrimitive.itemsForProcessing
                },
                getPinRelayOutput: (entry)=>{
                    if( isPeerFlowInstance(entry.sourcePrimitive) ){
                        return entry.sourcePrimitive.outputs?.[entry.sourcePin]?.data ?? []
                    }
                    if( receiver.type === "flowinstance"){
                        const fis = receiver.primitives.subfi.allItems.filter(d2=>Object.keys(d2._parentPrimitives ?? {}).includes(entry.sourcePrimitive.id))
                        return fis.flatMap(d2=>d2.outputs[entry.sourcePin]?.data)
                    }else if( receiver.origin.type === "flowinstance"){
                        return receiver.origin.inputs[entry.sourcePin]?.data ?? []
                    }
                    return []
                },
                getPinRelayString: (entry)=>{
                    if( isPeerFlowInstance(entry.sourcePrimitive) ){
                        const sourceOutputs = entry.sourcePrimitive.outputs
                        if( sourceOutputs?.[entry.sourcePin] ){
                            return {
                                value: sourceOutputs[entry.sourcePin]?.data,
                                config: "string",
                                useConfigOverride: "pass_through"
                            }
                        }
                        return undefined
                    }
                    const sourceInputs = entry.sourcePrimitive.inputs
                    if( sourceInputs[entry.sourcePin] ){
                        return {
                            value: sourceInputs[entry.sourcePin]?.data,
                            config: "string",
                            useConfigOverride: "pass_through"
                        }
                    }
                    return undefined
                },
                getAxisValues: (entry)=>{
                    const items = entry.sourcePrimitive.itemsForProcessing
                    const extents = CollectionUtils.mapCollectionByAxis(items, CollectionUtils.primitiveAxis(entry.sourcePrimitive, entry.axis, items)).extents.column
                    if(entry.inputMapConfig.types.includes("primitive")){
                        return {
                            values: extents.map(d=>d.primitive ?? obj.primitive(d.idx)).filter(d=>d),
                            passThroughConfig: "primitive"
                        }
                    }else{
                        const values = extents.filter(d=>d?.idx !=="_N_").map(d=>{
                            if( d.primitive ){
                                if(d.primitive.type === "category"){
                                    return `${d.primitive.title}: ${d.primitive.referenceParameters.description}`
                                }
                                return d.title
                            }
                            return d.label
                        })
                        return {
                            values,
                            passThroughConfig: "string"
                        }
                    }
                },
                getChildList: (entry)=>{
                    return entry.sourcePrimitive.itemsForProcessing
                },
                getFlowOrder: (entry)=>{
                    const flowInstance = entry.sourcePrimitive
                    if(flowInstance.type !== "flowinstance") return "1"
                    const rawParents = flowInstance._parentPrimitives ?? {}
                    const subfiParentId = Object.keys(rawParents).find(id=>(rawParents[id] ?? []).includes("primitives.subfi"))
                    const configParentId = Object.keys(rawParents).find(id=>(rawParents[id] ?? []).includes("primitives.config"))
                    let siblings
                    if(subfiParentId && configParentId){
                        const parentFlowInstance = obj.primitive(subfiParentId)
                        if(parentFlowInstance){
                            siblings = parentFlowInstance.primitives.subfi.allItems.filter(d=>{
                                const dRaw = d._parentPrimitives ?? {}
                                const dConfigParentId = Object.keys(dRaw).find(id=>(dRaw[id] ?? []).includes("primitives.config"))
                                return dConfigParentId === configParentId
                            })
                        }
                    }
                    if(!siblings && configParentId){
                        const configParent = obj.primitive(configParentId)
                        siblings = configParent?.primitives?.config?.allItems?.filter(d=>d.type === "flowinstance") ?? []
                    }
                    if(!siblings || siblings.length === 0) return "1"
                    const sorted = [...siblings].sort((a,b)=>String(a.plainId ?? a.id ?? "").localeCompare(String(b.plainId ?? b.id ?? ""), undefined, {numeric: true}))
                    const idx = sorted.findIndex(d=>d.id === flowInstance.id)
                    return `${idx >= 0 ? idx + 1 : 1}`
                }
            }

            // Spot datasetView-enabled wired sources (or their configParent
            // for flow-instance/template). The view itself usually doesn't end
            // up in resolved data — getImports walks past it — so remember the
            // datasetView config now and apply it after translateInputMap.
            // configParent is a sync proxy getter on the UI side.
            // Server-side mirrors this in InputHandler.fetchPrimitiveInputs.
            for (const entry of interim) {
                const sp = entry?.sourcePrimitive
                if (!sp) continue
                const own = sp.referenceParameters?.datasetView
                if (own?.enabled) { entry._datasetViewConfig = own; continue }
                const cp = sp.configParent
                const cpDv = cp?.referenceParameters?.datasetView
                if (cpDv?.enabled) entry._datasetViewConfig = cpDv
            }

            PrimitiveConfig.hydrateInputEntriesSync(interim, transformHelpers)
            let final = PrimitiveConfig.translateInputMap(interim)
            // datasetView unwrap is opt-in (consumers like the DSL
            // crystallizer call it explicitly). Generic UI consumers
            // (page+element rendering, etc.) need raw primitives.

            return final
        }
    },inputs(d, receiver, obj){
        return receiver.fetchInputs(undefined)
    },
    originTask(d, receiver, obj){
        let origin = receiver.origin
        if( origin ){
            if( ["experiment","activity"].includes(origin.type) ){
                return origin
            }
            return origin.findParentPrimitives({type: ["experiment", "activity"]})[0]
        }
        return undefined
    },
    importsHierarchy(d, receiver, obj){
        // Build minimal structure: relationships, applied filters, and counts only
        const createNode = (primitive)=>{
            let count
            const sourceCounts = { origin: 0, alt_origin: 0, import: 0 }

            if( primitive.type === "search" ){
                sourceCounts.origin = primitive.primitives.origin.allIds.length
                sourceCounts.alt_origin = primitive.primitives.alt_origin.allIds.length
                count = sourceCounts.alt_origin + sourceCounts.origin
                
            }else{
                const dataAtNode = primitive.itemsForProcessing
                count = dataAtNode.length
                sourceCounts.import = count
            }

            const params = primitive.referenceParameters ?? {}
            const filterInfo = {}
            if( params.referenceId !== undefined ){ filterInfo.referenceId = params.referenceId }
            if( params.type !== undefined ){ filterInfo.type = params.type }
            if( params.filters !== undefined ){ filterInfo.filters = params.filters }
            // Helper to normalize a filter, tolerating differing shapes
            const normalizeFilter = (f, isAxis = false)=>{
                if(!f) return undefined
                const value = (f.value !== undefined) ? f.value : (f.filter !== undefined ? f.filter : f.values)
                const faux = {
                    type: f.type,
                    value,
                    parameter: f.parameter ?? f.param,
                    access: f.access,
                    relationship: f.relationship
                }
                const cfg = PrimitiveConfig.commonFilterSetup(faux, isAxis)
                if(!cfg || cfg.skip || (cfg.check.length === 0 && cfg.includeNulls === false)) return undefined
                //if(!cfg || cfg.skip ) return undefined
                const {resolvedFilterType, pivot, relationship, check, includeNulls, isRange, invert, sourcePrimId} = cfg
                return { originalType: faux.type, parameter: faux.parameter, type: resolvedFilterType, pivot, relationship, includeNulls, isRange, values: check, invert: f.invert, sourcePrimId: f.sourcePrimId }
            }
           const exploreFilters = CollectionUtils.convertCollectionFiltersToImportFilters( primitive )
           const aggFilters = exploreFilters.map(d=>normalizeFilter(d))

            const node = { primitive, count, sourceCounts, children: [] }
            if( aggFilters.length > 0 ){ node.filters = aggFilters }
            return node
        }

        const root = createNode(receiver)
        const initialPath = new Set()
        if( receiver.id !== undefined ){
            initialPath.add(receiver.id)
        }
        const queue = [{ primitive: receiver, node: root, path: initialPath }]

        for( let idx = 0; idx < queue.length; idx++ ){
            const { primitive: currentPrimitive, node, path } = queue[idx]
            const imports = currentPrimitive?.primitives?.imports?.allItems ?? []
            for(const child of imports){
                if( !child ){
                    continue
                }
                const childNode = createNode(child)
                node.children.push(childNode)
                node.sourceCounts.import += childNode.count
                const childId = child.id
                if( childId !== undefined && path.has(childId) ){
                    childNode.cycle = true
                    continue
                }
                const nextPath = new Set(path)
                if( childId !== undefined ){
                    nextPath.add(childId)
                }
                queue.push({ primitive: child, node: childNode, path: nextPath })
            }
        }

        return root
    },
    importsHierarchyAnnotated(d, receiver, obj){
        // Build base structure first
        const root = receiver.importsHierarchy
        // Walk and annotate nodes with metadata and nodeTypes
        const q = [root]
        while(q.length){
            const node = q.shift()
            const primitive = node.primitive
            // Basic node info
            node.node = { id: primitive.id, plainId: primitive.plainId, title: primitive.title }
            delete node["primitive"]

            // Build nodeTypes aggregation
            let refIds = [], metadata = {}
            if( primitive.referenceParameters?.viewCategory ){
                refIds.push( ...[primitive.referenceParameters?.viewCategory].flat().filter(Boolean))
            }
            if( primitive.type === "search" ){
                const originPrimitives = primitive.primitives.origin.allItems
                const alOriginPrimitives = primitive.primitives.alt_origin.allItems
                refIds = [...originPrimitives.map(d=>d.referenceId), ...alOriginPrimitives.map(d=>d.referenceId)]
            }else{
                const dataAtNode = primitive.itemsForProcessing
                refIds = dataAtNode.map(d=>d.referenceId)
            }
            const nodeTypes = refIds.reduce((a,rid)=>{
                if( !a[rid] ){
                    const category = obj.category(rid)
                    a[rid] = { type: category.title, count: 0 }
                    metadata = {
                        ...metadata,
                        ...category.parameters
                    }
                }
                a[rid].count++
                return a
            }, {})
            node.nodeTypes = nodeTypes

            if( node.filters ){
                node.filters = node.filters.map(f=>{
                    if( f.type === "parent" ){
                        let outDescription = {
                            relationship: "descendant of"
                        }

                        const targetPrimitives = f.values.map(d=>obj.primitive(d))
                        const targets = []

                        if( f.sourcePrimId ){
                            const sourcePrim = obj.primitive(f.sourcePrimId)
                            if( sourcePrim ){
                                outDescription.categorization = {
                                    id: sourcePrim.id,
                                    plainId: sourcePrim.plainId,
                                    title: sourcePrim.title
                                }
                            }
                            outDescription.relationship = "categorized as"
                            if( f.includeNulls){
                                targets.push("Uncategorized")
                            }
                            targets.push(...targetPrimitives.map(d=>({id: d.id, plainId: d.plainId, category_title: d.title, category_detail: d.referenceParameters.description})))
                        }else{
                            targets.push(...targetPrimitives.map(d=>({id: d.id, plainId: d.plainId, title: d.title, type: d.metadata?.title})))
                        }
                        if( f.invert ){
                            outDescription.exclude_categories = targets
                        }else{
                            outDescription.include_categories = targets
                        }
                        return outDescription
                    }else if(f.type === "parameter"){
                        const paramInfo = metadata[f.parameter] ?? {}
                        let outDescription = {
                            parameter: paramInfo.title ?? f.parameter,
                            description: paramInfo.description
                        }
                         if( f.invert ){
                            outDescription.exclude_values = f.values
                        }else{
                            outDescription.include_values = f.values
                        }
                        return outDescription
                    }else if(f.type === "structured_summary"){
                        return {
                            parameter: f.parameter ?? f.param ?? "Structured",
                            description: "Structured summary field"
                        }
                    }
                    return f
                })
            }


            // queue children
            for(const c of node.children){ q.push(c) }
        }
        return root
    },
    _parentPrimitives(d, receiver, obj){
        return d._parentPrimitives
    },
    parentPrimitives(d, receiver, obj){
        const parents = []
        receiver.parentPrimitiveIds.forEach(d=>{
            const p = obj.primitive(d)
            if( p ){
                parents.push( p )
            }
        })
        return parents
    },
    parentPrimitiveIds(d, receiver, obj){
        if( d._ppIdCache ){
            return d._ppIdCache
        }
        const rawParents = d._parentPrimitives
        if( rawParents){
            for(const [p, v] of Object.entries(rawParents)){
                if( Array.isArray(v) ){
                    if( v.some(d=>!d)){
                        console.warn(`parentPrimtives of ${d.id} / ${d.plainId} has empty nodes`)
                        console.log(rawParents)
                    }
                }
            }
        }

        d._ppIdCache = rawParents ? Object.keys(rawParents).filter((p)=>rawParents[p]?.length > 0 && !rawParents[p].includes("primitives.imports") && !rawParents[p].includes("primitives.chat") && !rawParents[p].find(d=>d.startsWith("primitives.inputs."))) : []
        return d._ppIdCache
    },
    parentPrimitiveIdsAsSource(d, receiver, obj){
        let out = []
        if( d._parentPrimitives ){
            out = Object.keys(d._parentPrimitives).filter(k=>{
                return d._parentPrimitives[k].some(d => d.startsWith("primitives.source"))
            })
        }
        return out
    },
    parentPrimitivesAsSource(d, receiver, obj){
        return receiver.parentPrimitiveIdsAsSource.map(d=>obj.primitive(d))
    },
    parentPrimitiveIdsWithRelationship(d, receiver, obj){
        return (relationship, options = {})=>{
            let ids = []
            let rel, rId 
            if( relationship ){
                if( typeof(relationship) === "string"){
                    [rel, rId] = relationship.split(":") 
                }else{
                    ({rel, rId} = relationship)
                }
            } 
            if( rId !== undefined){
                rId = parseInt(rId)
            }

            const pp = d._parentPrimitives
            if( pp ){
                if( rel === "origin_link_result"){
                    const keys = Object.keys(pp);
                    for (let i = 0, len = keys.length; i < len; i++) {
                        const k = keys[i];
                        const arr = pp[k];
                        if (!Array.isArray(arr) || arr.length === 0){
                            continue
                        }
                        for (let j = 0, len = arr.length; j < len; j++) {
                            const s = arr[j];
                            if (s.endsWith('.origin') || s.endsWith('.source')|| s.endsWith('.link') || s.startsWith('primitives.results.')) {
                                ids.push(k);
                                break
                            }
                        }
                    }
                }else{
                    const check = `.${rel}`
                    ids = Object.keys(pp).filter(k=>pp[k].some(d => d.endsWith(check)))
                }
            }

            if( ids.length === 0){
                return []
            }

            if( rId ){
                const filtered = []
                if( options.preferHydrated){
                    for(const id of ids){
                        const prim = obj.primitive(id)
                        if( prim?.referenceId === rId){
                            filtered.push( prim )
                        }
                    }
                }else{

                    for(const id of ids){
                        const prim = obj.primitive(id)
                        if( prim?.referenceId === rId){
                            filtered.push(id)
                        }
                    }
                }
                return filtered
            }

            return ids
        }
    },
    parentPrimitiveWithRelationship(d, receiver, obj){
        return (relationship, options = {})=>{
            const returned = receiver.parentPrimitiveIdsWithRelationship(relationship, {preferHydrated: true})
            if(!returned || returned.length === 0){
                return
            }
            if( returned.some(d=>typeof(d) == "object")){
                return returned
            }
            const out = []
            for(const d of returned){
                const p = obj.primitive(d)
                if( p ){
                    out.push(p)
                }
            }
            return out
            
        }
    },parentPrimitiveRelationships(d, receiver, obj){
        return receiver.parentPrimitives.reduce((o, p)=>{
            let rels = [receiver.parentRelationship(p)].flat()
            rels.forEach((rel)=>{
                o[rel] = o[rel] || []
                o[rel].push( p )
            })
            return o
        }, [])
    },
    validateParameter(d, receiver, obj){
        return function(parameterName, value){
            const metadata = receiver.metadata
            if(parameterName === "state"){
                return true
            }
            const parameters = {...receiver.metadata?.parameters, ...(receiver.origin?.childParameters || {}), ...(receiver.task?.itemParameters?.[receiver.referenceId] || {})}
            if( Object.keys(parameters).length === 0 ){
                return false
            }
            const root = parameterName.split('.')[0]
            if( !(root in parameters) ){
                if( root.slice(-2) === "Id"){
                    if( (root.slice(0, -2) in parameters) ){
                        if( value !== undefined){
                            return true
                        }
                    }
                }
                return false
            }
            return true
        }
    },
    workspace(d, receiver, obj){
        if( d.workspaceId ){
            return obj.workspace( d.workspaceId )
        }
        return undefined
    },
    action(d, receiver, obj){
        return function(name, options, callback){
            return obj.doPrimitiveAction(receiver, name, options,callback)
        }
    },
    sidebar(d, receiver, obj){
        return function(options){
            if( typeof obj.sidebarSelect !== "function"){
                return undefined
            }
            return obj.sidebarSelect(receiver, options)
        }
    },
    setLocalFlag(d){
        return function( fieldName, value ){
            d['_' + fieldName] = value
            return true
        }
    },
    setField(d, receiver, obj){
        const assignOrDefine = (target, key, nextValue) => {
            // Handles getter-only prototype fields (e.g. renderConfig) by defining writable own properties on demand.
            try{
                target[key] = nextValue
                return target[key]
            }catch(error){
                if( !(error instanceof TypeError) ){
                    throw error
                }
                Object.defineProperty(target, key, {
                    configurable: true,
                    enumerable: true,
                    writable: true,
                    value: nextValue
                })
                return target[key]
            }
        }
        return function( fieldName, value, callbackName, skip = false ){
            let node = d
            const fields = fieldName.split(".")
            let last = fields.pop()
            if( fieldName === "title"){
                receiver[TITLE_SKIP_FLAG] = !!skip
            }
            fields.forEach((f)=>{
                if( node[f] === undefined || typeof node[f] !== "object" || node[f] === null){
                    assignOrDefine(node, f, {})
                }
                node = node[f]
            })
            assignOrDefine(node, last, value)
            if(!skip){
                obj.controller.updateField( receiver, fieldName, value, callbackName || `set_field`  )
            }
            obj.triggerCallback("set_field", [receiver], fieldName, false )
            return true
        }
    },
    addToParameter(d, receiver){
        return function( parameterName, value, skip = false){
            return receiver.modifyParameter(parameterName, value, true, skip)
        }
    },
    removeFromParameter(d, receiver){
        return function( parameterName, value, skip = false){
            return receiver.modifyParameter(parameterName, value, false, skip)
        }
    },
    modifyParameter(d, receiver, obj){
        return function( parameterName, value, add, skip = false ){
            let target = receiver.referenceParameters 
            let set = parameterName.split(".")
            let last = set.pop()
            set.forEach((n)=>{
                const lastNode = target
                target = target[n]
                if( !target ){
                    lastNode[n] = {}
                    target = lastNode[n]
                }
            })
            if(target[last] && Array.isArray(target[last])){
                target[last] = target[last].filter(item=>item !== value)
            }else{
                target[last] = receiver.metadata?.parameters?.[last]?.default ?? []
            }
            if( add ){
                target[last].push( value )
            }
            if(!skip){
                obj.controller.modifyParameter( receiver, parameterName, value, add  )
            }
            return true
        }
    },
    setParameter(d, receiver, obj){
        return function( parameterName, value, skip = false, force = true){
            if( force || receiver.validateParameter(parameterName, value)){
                let target = receiver.referenceParameters 
                let set = parameterName.split(".")
                let last = set.pop()
                set.forEach((n)=>{
                    const lastNode = target
                    target = target[n]
                    if( !target ){
                        lastNode[n] = {}
                        target = lastNode[n]
                    }
                })
                target[last] = value
                if(!skip){
                    obj.controller.updateParameter( receiver, parameterName, value  )
                }
                return true
            }
            return false
        }
    },
    addParentRelationship(d, receiver){
        return function( parent, path ){
            const asString = receiver.primitives.flattenPath(path)
            if(!d._parentPrimitives){
                d._parentPrimitives = {}
            }
            if(!d._parentPrimitives[parent.id]){
                d._parentPrimitives[parent.id] = []
            }
            if( d._parentPrimitives[parent.id].includes(asString)){
                return
            }
            d._parentPrimitives[parent.id].push(asString)
            d._ppIdCache = undefined
        }
    },
    removeParentRelationship(d, receiver){
        return function( parent, path ){
            const asString = receiver.primitives.flattenPath(path)
            if(!d._parentPrimitives){
                d._parentPrimitives = {}
            }
            if(!d._parentPrimitives[parent.id]){
                d._parentPrimitives[parent.id] = []
            }
            d._parentPrimitives[parent.id] = d._parentPrimitives[parent.id].filter((item)=>item !== asString)
            d._ppIdCache = undefined
        }
    },
    addRelationshipAndWait(d, receiver, obj){
        return async function( target, path ){
            if( receiver.primitives.add( target.id, path )){
                target.addParentRelationship(receiver, path)
                await obj.controller.setRelationshipAndWait( receiver, target, path, true )
            }
        }
    },
    addRelationship(d, receiver, obj){
        return function( target, path, skip = false ){
            if( receiver.primitives.add( target.id, path )){
                target.addParentRelationship(receiver, path)
                if( !skip ){
                    obj.controller.setRelationship( receiver, target, path, true )
                }
            }
        }
    },
    removeRelationship(d, receiver, obj){
        return function( target, path, skip = false ){
            if( receiver.primitives.remove( target.id, path )){
                target.removeParentRelationship(receiver, path)
                if( !skip ){
                    obj.controller.setRelationship( receiver, target, path, false )
                }
            }
        }
    },
    moveRelationship(d, receiver, obj){
        return function( target, from, to ){
            if( receiver.primitives.move( target.id, from, to) ){
                target.removeParentRelationship(receiver, from)
                target.addParentRelationship(receiver, to)
                obj.controller.moveRelationship( receiver, target, from, to)
            }
        }
    },
    toggleRelationship(d, receiver, obj){
        return function( target, metric ){
            let anchor = receiver.primitives.fromPath(metric.path, true)
            let path = metric.path

            if( !Array.isArray(anchor) ){
                let k = 'positive'
                anchor = anchor[k]
                if( !anchor ){
                    console.warn(`Cant find 'positive' in list`)
                    return undefined
                }
                const rebuild = (node, key)=>{
                    let o = {}
                    if( node instanceof Object ){
                        const tk = Object.keys(node)[0]
                        o[tk] = rebuild( node[tk], key )
                    }else{
                        o[node] = key
                    }
                    return o
                }
                path = rebuild(path, k)
            }

            const oldRelationship = anchor.includes( target.id )
            let result
            if( oldRelationship ){
                result = anchor.remove( target.id ) 
                target.removeParentRelationship(receiver, path)
            }else{
                result = anchor.add( target.id) 
                target.addParentRelationship(receiver, path)
            }
            if( result ){
                obj.controller.setRelationship( receiver, target, path, !oldRelationship )
            }
            return anchor
        }
    },
    removeChildren(d, receiver, obj){
        return async function(allItems = false){
            let directs = allItems ? receiver.primitives.uniqueAllItems : [receiver.primitives.origin.uniqueAllItems, receiver.primitives.auto.uniqueAllItems].flat()
            directs = uniquePrimitives(directs).filter((item)=>!item.lock)
            let nested = [] 
            for(const child of directs ){
                nested = nested.concat(await child.removeChildren())
            }
            for(const prim of directs){
                await obj.removePrimitive( prim )
            }
            return nested
        }
    },
    master_type(d){
        return d[MASTER_TYPE]
    },
    stateInfo(d, receiver, obj){
        return (obj.stateInfo[d.type] || obj.stateInfo["default"])[d.state] || {title: undefined }
    },
    isTask(d){
        return d.type === "activity" || d.type === "experiment" || d.type === "board" || d.type === "analysis"
    },
    relationshipAtLevel(d, receiver){
        return function(original, level = 0) {
            if (level === 0) {
                return [receiver];
            }
            const rels = Array.isArray(original) ? original : [original];
            const depth = level === undefined ? rels.length : level;
            if (depth === 0) {
                return [receiver];
            }
            let current = [receiver];
            const firstRel = rels[0];
            for (let i = 0; i < depth && current.length; i++) {
                const relName = rels[i] ?? firstRel;
                const nextLevel = [];
                const seen = new Set();
                for (const prim of current) {
                    if (!prim) {
                        continue;
                    }
                    const parents = relName === "origin" ? [prim.origin] : prim.parentPrimitiveWithRelationship(relName);
                    if (!parents) {
                        continue;
                    }
                    for (const p of parents) {
                        if (!p || !p.id || seen.has(p.id)) {
                            continue;
                        }
                        seen.add(p.id);
                        nextLevel.push(p);
                    }
                }
                current = nextLevel;
            }

            return current;
        }
    },
    relationshipIdsForRelationship(d, receiver, obj){
        return function(original) {
            const ids = []
            const parentEntries = Object.entries(receiver._parentPrimitives ?? {})
            if( original === "origin_link_result"){
                for(let i = 0; i < parentEntries.length; i++){
                    const k = parentEntries[i][0]
                    const v = parentEntries[i][1]
                    if(!Array.isArray(v) || v.length === 0){
                        continue
                    }
                    for(let j = 0; j < v.length; j++){
                        const s = v[j]
                        if(!s){ continue }
                        if( s === 'primitives.origin' || s === 'primitives.source' || s === 'primitives.link' || s.startsWith('primitives.results.')){
                            ids.push(k)
                            break
                        }
                    }
                }
            }else{
                const check = `.${original}`
                for(let i = 0; i < parentEntries.length; i++){
                    const k = parentEntries[i][0]
                    const v = parentEntries[i][1]
                    if(!Array.isArray(v) || v.length === 0){
                        continue
                    }
                    for(let j = 0; j < v.length; j++){
                        const str = v[j]
                        if(str && str.endsWith(check)){
                            ids.push(k)
                            break
                        }
                    }
                }
            }
            return ids
        }
    },
    relationshipIdsAtLevel(d, receiver, obj){
        return function(original, level = 0) {
            if (level === 0) {
                return receiver.id ? [receiver.id] : [];
            }

            const rels = Array.isArray(original) ? original : [original];
            const depth = level === undefined ? rels.length : level;
            if (depth === 0) {
                return receiver.id ? [receiver.id] : [];
            }
            const parsedRels = rels.map(relValue=>{
                if(!relValue){
                    return {rel: undefined, rId: undefined}
                }
                if(typeof relValue === "string"){
                    const [relPart, idPart] = relValue.split(":")
                    return {
                        rel: relPart,
                        rId: idPart !== undefined ? parseInt(idPart) : undefined
                    }
                }
                return {
                    rel: relValue.rel ?? relValue,
                    rId: relValue.rId !== undefined ? parseInt(relValue.rId) : undefined
                }
            })
            let current = [receiver];
            const firstRel = parsedRels[0];
            for (let i = 0; i < depth - 1 && current.length; i++) {
                const relInfo = parsedRels[i] ?? firstRel;
                const nextLevel = [];
                const seen = new Set();
                for (const prim of current) {
                    if (!prim) {
                        continue;
                    }
                    const parents = relInfo.rel === "origin" ? [prim.origin] : prim.parentPrimitiveWithRelationship(relInfo.rel );
                    if (!parents) {
                        continue;
                    }
                    for (const p of parents) {
                        if (!p || !p.id || seen.has(p.id)) {
                            continue;
                        }
                        if( relInfo.rId && p.referenceId != relInfo.rId){
                            continue
                        }
                        seen.add(p.id);
                        nextLevel.push(p);
                    }
                }
                current = nextLevel;
            }

            let finalRel = parsedRels[depth - 1] ?? firstRel;
            
            const resultIds = [];
            const seenIds = new Set();
            for (const prim of current) {
                if (!prim) {
                    continue;
                }
                let ids;
                if (finalRel.rel === "origin") {
                    const id = prim.origin?.id;
                    ids = id ? [id] : [];
                }else{
                    ids = prim.parentPrimitiveIdsWithRelationship(finalRel ) ?? [];
                }
                if( finalRel.rId ){
                    for (const id of ids) {
                        if (id && !seenIds.has(id)) {
                            seenIds.add(id);
                            const p = obj.primitive(id)
                            if( p?.referenceId == finalRel.rId){
                                resultIds.push(id);
                            }
                        }
                    }
                }else{
                    for (const id of ids) {
                        if (id && !seenIds.has(id)) {
                            seenIds.add(id);
                            resultIds.push(id);
                        }
                    }
                }
            }
            return resultIds;
        }
    },
    metadata(d, receiver, obj){
        let category = obj.category(d.referenceId)
        if( category === undefined){
            category = PrimitiveConfig.metadata[receiver.type]
        }
        return category
    },
    nestedItems(d, receiver){
        if( d.type === "view"){
            return uniquePrimitives( receiver.primitives.allSegment.map(item=>item.nestedItems).flat() )
        }
        if( d.type === "segment"){
            const segmentItems = (node)=>{
                return node.primitives.uniqueAllItems.map((child)=>{
                    if( child.type === "segment" ){
                        return segmentItems( child )
                    }
                    return child
                }).flat()
            }
            return segmentItems( receiver).filter((item)=>!item.referenceParameters?.duplicate)
        }
        return []
    },
    metrics(d, receiver, obj){
        if(!d.metrics){ return undefined}
        return d.metrics.map((m)=>{
            return new Proxy({parent: receiver, metric: m}, obj.metricResolver)
        })
    },
    task(d, receiver){
        if( receiver.isTask ){
            return receiver
        }else{
            return receiver.origin?.task
        }
    },
    addresses(d, receiver){
        if( d.type !== "hypothesis"){
            return undefined
        }
        return receiver.findParentPrimitives({type: "assessment"}).map((a)=>{
            const component_ids = a.primitives.paths(receiver.id, '.hfc.').map((entry)=>entry.match(/\.hfc\.(\d+)/)?.[1])
            const components = component_ids.map((id)=>({framework: a.framework, component: a.framework?.components[id]}))
            return components
        }).flat().filter((item,idx,arr)=>arr.findIndex((other)=>other.component.id === item.component.id)===idx)
    },
    addresses_lenses(d, receiver){
        if( d.type !== "hypothesis"){
            return undefined
        }
        return receiver.addresses.map((c)=>{
            if( c.framework ){
                return c.framework.lenses[ c.component.lens]
            }
            return undefined
        }).filter((item,idx,arr)=>item && arr.indexOf(item)===idx)
    },
    addresses_components(d, receiver){
        if( d.type !== "hypothesis"){
            return undefined
        }
        return receiver.addresses.map((c)=>{
            const lens = c.framework.lenses[ c.component.lens]
            return {...c.component, lens}
        }).filter((item,idx,arr)=>arr.findIndex((other)=>other.id === item.id)===idx)
    },
    framework(d, receiver, obj){
        if( d.type !== "assessment"){
            return undefined
        }
        return obj.framework( d.frameworkId)
    },
    venture(d, receiver){
        if( d.type !== "assessment"){
            return undefined
        }
        return receiver.parentPrimitives.filter((item)=>item.type === "venture")[0]
    },
    currentAssessment(d, receiver){
        if( d.type !== "venture" ){
            return undefined
        }
        return receiver.primitives.allUniqueAssessment.pop()
    },
    categories(d, receiver){
        return receiver.parentPrimitives.filter((item)=>item.type === "category")
    },
    doDiscovery(d, receiver, obj){
        return (ids)=>obj.doPrimitiveDocumentDiscovery( receiver, ids )
    },
    doQuestionsAnalysis(d, receiver, obj){
        return (ids)=>obj.doPrimitiveDocumentQuestionsAnalysis( receiver, ids )
    },
    analyzer(d, receiver, obj){
        if( d.type === "result" || d.type === "evidence" || d.type === "experiment" || d.type === "task" ){
            return ()=>{
                let resultAnalyzer = ResultAnalyzer(receiver)
                resultAnalyzer.init()
                return resultAnalyzer
            }
        }
        if( d.type === "assessment" ){
            return ()=>{
                let assessmentAnalyzer = AssessmentAnalyzer(receiver)
                assessmentAnalyzer.init()
                return assessmentAnalyzer
            }
        }
        return undefined
    },
    getDocumentAsText(d, receiver, obj){
        return ()=>obj.getPrimitiveDocumentAsText( receiver )
    },
    getDocument(d, receiver, obj){
        return ()=>obj.getPrimitiveDocument( receiver )
    },
    displayType(d){
        return d.metadata?.title ?? d.type.charAt(0).toUpperCase() + d.type.slice(1)
    },
    context(d, receiver){
        const category = receiver.metadata
        if( !category?.ai?.process?.context){
            if( receiver.type === "evidence"){
                if( receiver.referenceParameters?.quote){
                    return `${category.title}: ${receiver.title}\nQuote:"${receiver.referenceParameters?.quote.replaceAll(/\n/g,". ")}"`
                }
                if( receiver.origin.referenceId === PrimitiveConfig.Constants.QUERY_RESULT ){                                
                    const parts = [
                        receiver.origin.referenceParameters?.description ? `Context:${receiver.origin.referenceParameters?.description.replaceAll(/\n/g,". ")}` : undefined, 
                        receiver.origin.referenceParameters?.quote ? `Quote:${receiver.origin.referenceParameters?.quote.replaceAll(/\n/g,". ")}` : undefined
                    ].filter(Boolean)
                    if( parts.length > 0){
                        return `${category.title}: ${receiver.title}\n${parts.join("\n")}`
                    }
                }
                return receiver.title
            }
            return undefined
        }
        let out = ""
        for(const key of Object.keys(category.ai.process.context.fields ?? [])){
            const source = category.ai?.process?.context.fields[key]
            if( source instanceof Object){
                if( source.type === "fixed" ){
                    const value = source.value
                    if( value !== undefined && value !== null && `${value}`.length > 0 ){
                        const header = source.title ?? key
                        if( out.length > 0 && !out.endsWith("\n") ){
                            out += "\n"
                        }
                        out += header?.length > 0 ? `${header}: ${value}\n` : `${value}\n`
                    }
                    continue
                }
                if( source.referenceId || source.target){
                    let header = source.title
                    let showCount = false
                    let children = source.path ? receiver.primitives[source.target].uniqueAllItems : receiver.primitives.uniqueAllItems
                    if( source.referenceId ){
                        children = children.filter(item=>item.referenceId === source.referenceId)
                    }
                    if( children && children.length > 0){
                        if( out.length > 0){
                            out += ".\n"
                        }
                        out += (header?.length > 0 ? `${header}:` : "") + children.map((child,i)=>{
                            let interim = `${(source.prefix ? source.prefix + " " : "") ?? ""}${showCount ? i + " - " : ""} ${child.title}`
                            let fields = source.fields ?? Object.keys(child.referenceParameters ?? {})
                            for(const field of fields){
                                const val = [child.referenceParameters[field]].flat().filter(Boolean)
                                if( val.length > 0){
                                    interim += `\n${field}: ${val.join(", ")}`
                                }
                            }
                            return interim
                        }).join("\n") + "\n"
                    }else if( source.fallback){
                        const param = source.fallback.slice(7)
                        if( receiver.referenceParameters?.[param] ){
                            out += (header?.length > 0 ? `${header}: ` : "") + receiver.referenceParameters[param] + "\n"
                        }
                    }
                }else{
                    const list = [receiver.referenceParameters?.[key]].flat().filter(Boolean)
                    const unpackNested = (items, src)=>{
                        if( items.length === 0){
                            return
                        }
                        out += (src.header ?? key) + ":\n"
                        const titleBase = src.title ?? key
                        const subFields = (src.fields ?? []).filter(item=>item instanceof Object)
                        const contentFields = (src.fields ?? []).filter(item=>!(item instanceof Object))
                        for(const entry of items){
                            const title = titleBase.replace(/\{([^}]+)\}/g, (match, fieldName) => fieldName in entry ? entry[fieldName] : match);
                            out += title + ":" + contentFields.map(fld=>entry[fld]).join("") + "\n"
                            for(const sf of subFields){
                                const sublist = entry[sf.source]
                                if( sublist ){
                                    unpackNested(sublist, sf )
                                }
                            }
                        }
                    }
                    unpackNested(list, source)
                }
            }else{
                let field
                if( key === "title"){
                    field = receiver.title
                }else{
                    field = receiver.referenceParameters?.[key]
                }
                if( field ){
                    let header = source
                    out += header?.length > 0 ? `${header}: ${field}\n` : `${field}\n`
                }
            }
        }
        return out.length === 0 ? undefined : out
    },
    findImportRoute(d, receiver){
        return function(target, out = []){
            if( receiver.primitives.imports.allIds.includes(target)){
                out.push(receiver)
            }else{
                const segments = receiver.primitives.imports.allUniqueSegment
                for(const seg of segments){
                    seg.findImportRoute( target, out)
                }                        

                const workings = receiver.parentPrimitives.filter(item=>item.type === "working")
                for(const work of workings){
                    work.findImportRoute( target, out)
                }                        
            }
            return out
        }
    },
    importedBy(d, receiver, obj){
        return Object.keys(receiver._parentPrimitives).filter(id=>receiver._parentPrimitives[id].includes("primitives.imports")).map(id=>obj.primitive(id))
    },
    findRouteToParent(d, receiver){
        return function(target){
            const ids = {}
            const layerUp = (node)=>{
                let out
                if( node.parentPrimitiveIds.length === 0){
                    return false
                }
                for(const parent of node.parentPrimitives){
                    if(parent.id === target){
                        return [parent]
                    }
                }
                node.parentPrimitives.forEach((parent)=>{
                    if( !ids[parent.id] ){
                        ids[parent.id] = true
                        if(!out){
                            const result = layerUp( parent )
                            if( result ){
                                out = [...result, parent]
                            }
                        }
                    }
                })
                return out   
            }
            return layerUp(receiver)
        }
    },
    follow(d, receiver){
        return function(steps, cache){
            let scope = [receiver]
            if( receiver.type === "categorizer"){
                scope = receiver.primitives.origin.allItems
            }
            let out = [receiver]
            if( steps ){
                for(const step of steps){
                    out = doStep(out, step, scope, cache)
                }
            }
            return out
        }
    },
    findParentPrimitives(d, receiver){
        return function(options = {type: undefined, referenceId: undefined, first: false}){
            const checked = new Set()
            const scatter = (list)=>{
                if( list === undefined){ return []}
                return uniquePrimitives(list.flatMap((p) => p.parentPrimitives)).filter(item=>{
                    if( checked.has(item.id) ){
                        return false
                    }
                    checked.add(item.id)
                    return true
                })
            }
            let found = []
            let current = scatter( [receiver] )
            if( typeof(options.type) == "string"){
                options.type = [options.type]
            }
            while( current.length > 0){
                if (options.type === undefined && options.referenceId === undefined) {
                    found.push(...current);
                } else {
                    let filtered = current;
                    if (options.referenceId !== undefined && options.referenceId.length > 0) {
                        filtered = filtered.filter((p) => options.referenceId.includes(p.referenceId));
                    }
                    if (options.type !== undefined && options.type.length > 0) {
                        filtered = filtered.filter((p) => options.type.includes(p.type));
                    }
                    found.push(...filtered);
                }
                if( options.first && found.length > 0 ){
                    return found
                }
                current = scatter( current )
            }
            return uniquePrimitives(found)
        }
    },
    parentRelationship(d, receiver, obj){
        return function( parent, root ){
            if( !(parent instanceof(Object)) ){
                parent = obj.primitive(parent)
            }
            return receiver.parentPaths(parent, root)?.map((path)=>path.split('.').slice(-1)[0])
        }
    },
    configParent(d, receiver){
        return receiver.parentPrimitiveWithRelationship("config")?.[0]
    },
    getConfig(d, receiver, obj){
        const proxySource = resolveProxySourcePrimitive(receiver, obj);
        if( proxySource ){
            const base = proxySource._getConfigWithFields();
            const localConfig = receiver.referenceParameters || {};
            const merged = {
                ...base,
                ...localConfig,
            };
            if (base?.explore || localConfig?.explore) {
                merged.explore = mergeExploreConfig(base?.explore ?? {}, localConfig?.explore ?? {});
            }
            return merged;
        }
        return receiver._getConfigWithFields();
    },
    _getConfigWithFields(d, receiver, obj){
        const proxySource = resolveProxySourcePrimitive(receiver, obj);
        if( proxySource ){
            return (requiredFields, doneFields = new Set()) => {
                const base = proxySource._getConfigWithFields(requiredFields, doneFields);
                const localConfig = receiver.referenceParameters || {};
                const merged = {
                    ...base,
                    ...localConfig,
                };
                if (base?.explore || localConfig?.explore) {
                    merged.explore = mergeExploreConfig(base?.explore ?? {}, localConfig?.explore ?? {});
                }
                return merged;
            }
        }
        return (requiredFields, doneFields = new Set() )=>{
            const localConfig = {}
            const inputsConfig = {}
            if( requiredFields == undefined){
                requiredFields = new Set([
                    ...Object.keys(receiver.referenceParameters ?? {}),
                    ...Object.keys(receiver.metadata?.parameters ?? {})
                ])                            
            }

            Object.keys(receiver.referenceParameters).forEach(field => {
                if( !doneFields.has( field ) ){
                    localConfig[field] = receiver.referenceParameters[field]
                    requiredFields.delete(field)
                    doneFields.add(field)
                }
            });
            
            if( requiredFields.size > 0){
                const overrides = []
                const connectedInputs = Object.keys(receiver.primitives.inputs).map(item=>item.split("_")[1])
                Object.keys(receiver.metadata?.pins?.input ?? {}).forEach((pin=>{
                    if( receiver.metadata.pins.input[pin]?.override && connectedInputs.includes(pin) && !doneFields.has(pin)){
                        overrides.push({
                            input:pin,
                            param:receiver.metadata.pins.input[pin]?.override
                        })
                    } 
                }))
                if( overrides.length > 0){
                    const inputs = receiver.inputs
                    for(const override of overrides ){
                        if( inputs[override.input] && inputs[override.input].data !== undefined){
                            if( typeof( inputs[override.input].data) !== "string" || inputs[override.input].data.trim().length > 0){
                                inputsConfig[override.param] = inputs[override.input].data
                                requiredFields.delete(override.param)
                                doneFields.add(override.param)
                            }
                        }
                    }
                }
            }

            const base = receiver.getConfigWithoutOverrides( requiredFields, doneFields )
            const merged = {
                ...base,
                ...inputsConfig,
                ...localConfig,
            }
            if (base?.explore || localConfig?.explore) {
                merged.explore = mergeExploreConfig(base?.explore ?? {}, localConfig?.explore ?? {});
            }
            return merged
        }
    },
    getConfigWithoutOverrides(d, receiver, obj){
        const proxySource = resolveProxySourcePrimitive(receiver, obj);
        if( proxySource ){
            return (requiredFields, doneFields = new Set()) => {
                const base = proxySource.getConfigWithoutOverrides(requiredFields, doneFields);
                const localConfig = receiver.referenceParameters || {};
                const merged = {
                    ...base,
                    ...localConfig,
                };
                if (base?.explore || localConfig?.explore) {
                    merged.explore = mergeExploreConfig(base?.explore ?? {}, localConfig?.explore ?? {});
                }
                return merged;
            }
        }
        return (requiredFields, doneFields = new Set() )=>{
            if( !requiredFields ){
                requiredFields = new Set( Object.keys(receiver.metadata?.parameters ?? {}))
            }
            const category = receiver.metadata
            let categoryConfig = {} 

            for(const cKey of  [
                ...Object.keys(receiver.referenceParameters ?? {}),
                ...Object.keys(receiver.metadata?.parameters ?? {})
            ]){
                if( !doneFields.has(cKey)){
                    requiredFields.add(cKey)
                }
            }

            for(const p of Object.keys(category?.parameters ?? {})){
                if(  category.parameters[p].default && !doneFields.has(p) ){
                    categoryConfig[p] = category.parameters[p].default
                }
            }

            const localConfig = receiver.referenceParameters || {};
            let parentConfig = {};
            const configParent = receiver.configParent
            if( configParent ){
                parentConfig = configParent._getConfigWithFields(requiredFields, doneFields)
                Object.keys(parentConfig).forEach(field => {
                    requiredFields.delete(field)
                    doneFields.add(field)
                });
            }

            const merged = {
                ...categoryConfig,
                ...parentConfig,
                ...localConfig,
            };
            if (categoryConfig?.explore || parentConfig?.explore || localConfig?.explore) {
                const baseExplore = mergeExploreConfig(categoryConfig?.explore ?? {}, parentConfig?.explore ?? {});
                merged.explore = mergeExploreConfig(baseExplore, localConfig?.explore ?? {});
            }
            return merged;
        }
    },
    parentPaths(d, receiver, obj){
        return function( parent, root ){
            if( !(parent instanceof(Object)) ){
                parent = obj.primitive(parent)
            }
            let out = d._parentPrimitives[parent.id]
            if( out ){
                out = out.map(item=>item.slice(11))
                if( root ){
                    const len = root.length
                    out = out.filter((path)=>path.substr(0, len) == root)
                }
            }
            return out
        }
    }
}

function doStep(data, step, scope, cache){
    let out = data
    const instructions = Object.keys(step) 
    for(const instruction of instructions){
        const config = step[instruction]
        switch(instruction){
            case "fetch_items":{
                let cacheKey
                if( cache && out.length === 1){
                    cacheKey = `fetch_items-${out[0].id}-${config.referenceId}`
                    if( cache[cacheKey] ){
                        return cache[cacheKey]
                    }
                }
                let temp = out.flatMap(d=>d.itemsForProcessing).filter(d=>d)
                if( config.referenceId ){
                    temp = temp.filter(d=>d.referenceId === config.referenceId)
                }
                out = temp
                if( cacheKey ){
                    cache[cacheKey] = out
                }
                break
            }
            case "fetch_children":{
                let cacheKey
                if( cache && out.length === 1){
                    cacheKey = `fetch_children-${out[0].id}-${config.referenceId}`
                    if( cache[cacheKey] ){
                        return cache[cacheKey]
                    }
                }
                let temp = out.flatMap(d=>[...d.primitives.origin.allItems,...d.primitives.results.allItems]).filter(d=>d)
                if( config.referenceId ){
                    temp = temp.filter(d=>d.referenceId === config.referenceId)
                }
                out = temp
                if( cacheKey ){
                    cache[cacheKey] = out
                }
                break
            }
            case "fetch_ancestor":{
                if( config.referenceId ){
                    const r = [config.referenceId]
                    out = out.flatMap(d=>d.findParentPrimitives({referenceId: r}))
                }else{
                    out = out.map(d=>d.origin).filter(d=>d)
                }
                break
            }
            case "filter":{
                let categoryIds
                if( config.category_label ){
                    const comp = [config.category_label].flat()
                    let categories //= uniquePrimitives(out.flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category")))
                    if( scope && scope.length > 0){
                        categories = scope.flatMap(d=>d.primitives.origin.allItems)
                    }else{
                        categories = uniquePrimitives(out.flatMap(d=>d.parentPrimitives.filter(d=>d.type === "category")))
                    }
                    categoryIds = categories.filter(d=>comp.includes(d.title )).map(d=>d.id)
                }
                out = out.filter(d=>{
                    let inScope = d
                    if( config.fetch_children ){
                        inScope = doStep( [d], {fetch_children: config.fetch_children}, scope, cache)
                        if( config.count !== undefined){
                            return inScope.length === config.count
                        }
                    }
                    if( categoryIds){
                        return Object.keys(d._parentPrimitives ?? {}).find(d=>categoryIds.includes(d))
                    }

                })
                break
            }
        }
    }
    return out
    
}


let instance = undefined
function MainStore (prims){
    if( !prims && instance ){
        return instance
    }
    window.contactHelper = ContactHelper()
    let obj = {
        id:  Math.floor(Math.random() * 99999),
        callbacks: {},
        types: PrimitiveConfig.types,
        loadStatus: {phase: 'idle', message: '', current: 0, total: 0},
        onLoadProgress: undefined,
        primitiveLoadController: undefined,
        inflightFlowFocusLoads: new Map(),
        loadDebugEnabled(){
            try{
                return window?.__DEBUG_PRIMITIVE_LOADS__ === true || window?.localStorage?.getItem("debug:primitive-loads") === "1"
            }catch(error){
                return false
            }
        },
        logLoadDebug(event, payload = {}){
            if( !this.loadDebugEnabled() ){
                return
            }
            const context = {
                storeId: this.id,
                activeWorkspaceId: this.activeWorkspaceId,
                loadedWorkspaceId: this.loadedWorkspaceId,
                loadedWorkspaceMode: this.loadedWorkspaceMode,
                loadedWorkspaceFocusId: this.loadedWorkspaceFocusId
            }
            console.log(`[primitive-load] ${new Date().toISOString()} ${event}`, {...context, ...payload})
        },
        beginPrimitiveLoad(reason = "unknown"){
            this._loadTokenCounter ||= 0
            const nextToken = ++this._loadTokenCounter
            const previous = this.primitiveLoadController
            if( this.primitiveLoadController ){
                this.logLoadDebug("abort_existing", {
                    previousToken: previous?._loadToken,
                    previousReason: previous?._loadReason,
                    nextToken,
                    nextReason: reason
                })
                try{
                    this.primitiveLoadController.abort()
                }catch(err){
                    // no-op
                }
            }
            this.primitiveLoadController = new AbortController()
            this.primitiveLoadController._loadToken = nextToken
            this.primitiveLoadController._loadReason = reason
            this.logLoadDebug("begin", {token: nextToken, reason})
            return this.primitiveLoadController
        },
        endPrimitiveLoad(controller){
            this.logLoadDebug("end", {
                token: controller?._loadToken,
                reason: controller?._loadReason,
                active: this.primitiveLoadController === controller
            })
            if( this.primitiveLoadController === controller ){
                this.primitiveLoadController = undefined
            }
        },
        withFlowFocusLoad: async function(focusId, fn){
            if( !focusId ){
                return await fn()
            }
            const key = `flowinstance:${focusId}`
            const inflight = this.inflightFlowFocusLoads.get(key)
            if( inflight ){
                this.logLoadDebug("flow_focus_lock:reuse", {focusId})
                return await inflight
            }
            const run = (async()=>await fn())()
            this.inflightFlowFocusLoads.set(key, run)
            this.logLoadDebug("flow_focus_lock:start", {focusId})
            try{
                return await run
            }finally{
                if( this.inflightFlowFocusLoads.get(key) === run ){
                    this.inflightFlowFocusLoads.delete(key)
                    this.logLoadDebug("flow_focus_lock:end", {focusId})
                }
            }
        },
        updateLoadStatus(update = {}) {
            const nextStatus = {...this.loadStatus, ...update}
            this.loadStatus = nextStatus
            if( typeof this.onLoadProgress === 'function'){
                this.onLoadProgress(nextStatus)
            }
        },
        waitForPrimitive:async function(id, count = 1){
            console.log(`checking for ${id}`)
            const primitive = obj.primitive(id)
            if( primitive === undefined){
                console.log(`Primitive ${id} not present - sleeping`)
                await new Promise(r => setTimeout(r, 100 * count ));
                if( count < 10 ){
                    console.log(`Try again`)
                    return await this.waitForPrimitive(id, count + 1)
                }
            }
            return primitive
        },
        loadHomeScreenPrimitives: async function(id){
            obj.loadControl(false)
            const response = await fetch(`/api/primitives`)
            await obj.consumePrimitiveStream(response, {reset: false, trackProgress: true})
            obj.loadControl(true)
            obj.homescreenReady = true
            obj.updateLoadStatus({phase: 'idle', message: '', current: 0, total: 0})
            return true
        },
        loadWorkspaceFor:async function(id, options = {}){
            const mode = options.mode ?? "workspace"
            const focus = options.focusId ?? id
            const focusLockId = mode === "flowinstance" && focus ? `${focus}` : undefined
            return await obj.withFlowFocusLoad(focusLockId, async ()=>{
            const background = options.background === true
            const controller = obj.beginPrimitiveLoad(`loadWorkspaceFor:${id}:${mode}:${focus}`)
            const params = new URLSearchParams({
                owns: `${id}`,
                debug: "1"
            })
            if( mode && mode !== "workspace"){
                params.set("mode", mode)
            }
            if( mode === "flowinstance" && focus){
                params.set("focus", `${focus}`)
            }
            if( !background ){
                obj.loadControl(false)
            }
            const url = `/api/primitives?${params.toString()}`
            const startedAt = Date.now()
            const callStack = obj.loadDebugEnabled() ? `${new Error().stack ?? ""}`.split("\n").slice(1, 6).map(line => line.trim()) : undefined
            obj.logLoadDebug("loadWorkspaceFor:start", {id, mode, focus, background, url, token: controller?._loadToken, callStack})
            try{
                const response = await fetch(url, {signal: controller.signal})
                obj.logLoadDebug("loadWorkspaceFor:response", {id, mode, focus, status: response.status, ok: response.ok, token: controller?._loadToken})
                await obj.consumePrimitiveStream(response, {reset: true, trackProgress: true})
                obj.logLoadDebug("loadWorkspaceFor:stream_complete", {id, mode, focus, elapsedMs: Date.now() - startedAt, token: controller?._loadToken})
            }catch(error){
                if( error?.name === "AbortError" ){
                    obj.logLoadDebug("loadWorkspaceFor:aborted", {id, mode, focus, elapsedMs: Date.now() - startedAt, token: controller?._loadToken})
                    return false
                }
                obj.logLoadDebug("loadWorkspaceFor:error", {id, mode, focus, message: error?.message ?? `${error}`, token: controller?._loadToken})
                throw error
            }finally{
                obj.endPrimitiveLoad(controller)
                if( !background ){
                    obj.loadControl(true)
                }
            }

            let primitive = obj.primitive(id)
            if( primitive === undefined){
                primitive = obj.primitiveByPlain(parseInt(id))
                if( primitive === undefined){
                    throw `Couldnt load ${id}`
                }
            }
            obj.activeWorkspaceId = primitive.workspaceId
            obj.loadedWorkspaceId = primitive.workspaceId
            obj.loadedWorkspaceMode = mode
            obj.loadedWorkspaceFocusId = mode === "flowinstance" ? `${focus}` : undefined
            obj.joinChannel(obj.activeWorkspaceId)
            obj.logLoadDebug("loadWorkspaceFor:complete", {id, resolvedWorkspaceId: primitive.workspaceId, mode, focus, token: controller?._loadToken})
            obj.updateLoadStatus({phase: 'idle', message: '', current: 0, total: 0})
            return true
            })
        },
        joinChannel:async function(newChannel){
            if( obj.socket ){
                obj.socket.emit("room", newChannel)
            }
        },
        fetchPrimitivesBatch:async function(ids){
            // Deduplicate: skip IDs already in store or already being fetched
            const missing = (ids || []).filter(id => !obj.primitive(id) && !obj._fetchingBatch?.has(id));
            if (missing.length === 0) return;
            obj._fetchingBatch ||= new Set();
            missing.forEach(id => obj._fetchingBatch.add(id));
            try {
                const result = await fetch(`/api/primitives/fetch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids: missing })
                });
                const response = await result.json();
                if (response.success && Array.isArray(response.result)) {
                    for (const p of response.result) {
                        const id = p._id || p.id;
                        obj.data.primitives[id] = primitive_access(p, "primitive");
                    }
                }
            } finally {
                missing.forEach(id => obj._fetchingBatch.delete(id));
                if (obj._fetchingBatch.size === 0) obj._fetchingBatch = null;
            }
        },
        fetchPrimitive:async function(primitiveId){
            if(obj.primitive(primitiveId)){
                return
            }
            if( obj.attemptedLoad?.[primitiveId]){
                return
            }
            obj.loadControl(false)
            obj.attemptedLoad ||= {}
            obj.attemptedLoad[primitiveId] = true
            const result = await fetch(`/published/fetch/${primitiveId}`)
            const response = await result.json()
            if( response.success){
                console.log(response.result)
                delete obj.attemptedLoad[primitiveId]
                obj.data.primitives[primitiveId] = primitive_access(response.result, "primitive")
            }
            obj.loadControl(true)
        },
        setActiveWorkspaceFrom:async function(primitive){
            if( !primitive.workspaceId ){
                console.warn(`No workspace for Primitive ${primitive.id}`)
                return
            }
            this.loadActiveWorkspace( primitive.workspaceId)

        },
        setActiveWorkspace:async function(id){
            obj.activeWorkspaceId = id
        },
        loadActiveWorkspace:async function(id, options = {}){
            const mode = options.mode ?? "workspace"
            const focusId = options.focusId !== undefined ? `${options.focusId}` : undefined
            const focusLockId = mode === "flowinstance" && focusId ? focusId : undefined
            return await obj.withFlowFocusLoad(focusLockId, async ()=>{
            const background = options.background === true
            obj.logLoadDebug("loadActiveWorkspace:called", {id, mode, focusId, background})
            if( !id ){
                obj.logLoadDebug("loadActiveWorkspace:skip_no_id", {mode, focusId})
                return
            }
            if( obj.loadedWorkspaceId === id ){
                if( mode === "flowinstance" ){
                    if( obj.loadedWorkspaceMode === "flowinstance" && obj.loadedWorkspaceFocusId === focusId ){
                        obj.logLoadDebug("loadActiveWorkspace:skip_already_loaded_focus", {id, mode, focusId})
                        return
                    }
                }else if( obj.loadedWorkspaceMode === "workspace" || obj.loadedWorkspaceMode === mode ){
                    obj.logLoadDebug("loadActiveWorkspace:skip_already_loaded_mode", {id, mode, focusId})
                    return
                }
            }
            if( obj.loadedWorkspaceId === id && obj.loadedWorkspaceMode === mode ){
                if( mode === "flowinstance" && obj.loadedWorkspaceFocusId !== focusId ){
                    // Different focus in same workspace+mode must refresh.
                }else{
                    obj.logLoadDebug("loadActiveWorkspace:skip_same_workspace_mode", {id, mode, focusId})
                    return
                }
            }
            if( mode === "flowinstance" && !focusId ){
                obj.logLoadDebug("loadActiveWorkspace:skip_missing_focus", {id, mode})
                return
            }
            const workspace = obj.workspace(id)
            if( !workspace ){
                console.warn(`Workspace ${id} not found for active user`)
                obj.logLoadDebug("loadActiveWorkspace:skip_workspace_not_found", {id, mode, focusId})
                return
            }
            const controller = obj.beginPrimitiveLoad(`loadActiveWorkspace:${id}:${mode}:${focusId ?? ""}`)
            if( !background ){
                obj.loadControl(false)
            }

            obj.activeWorkspaceId = id
            obj.logLoadDebug("loadActiveWorkspace:start", {id, mode, focusId, workspaceTitle: workspace.title, token: controller?._loadToken})

            if( !background ){
                obj.updateLoadStatus({phase: 'primitives', message: 'Loading workspace data...', current: 0, total: 0})
            }
            try{
                const params = new URLSearchParams({
                    workspace: `${obj.activeWorkspaceId}`
                })
                if( mode && mode !== "workspace"){
                    params.set("mode", mode)
                }
                if( mode === "flowinstance" && focusId){
                    params.set("focus", focusId)
                }
                const url = `/api/primitives?${params.toString()}`
                const startedAt = Date.now()
                const callStack = obj.loadDebugEnabled() ? `${new Error().stack ?? ""}`.split("\n").slice(1, 6).map(line => line.trim()) : undefined
                obj.logLoadDebug("loadActiveWorkspace:fetch", {id, mode, focusId, url, token: controller?._loadToken, callStack})
                const response = await fetch(url, {signal: controller.signal})
                obj.logLoadDebug("loadActiveWorkspace:response", {id, mode, focusId, status: response.status, ok: response.ok, token: controller?._loadToken})
                await obj.consumePrimitiveStream(response, {reset: true, trackProgress: true})
                obj.logLoadDebug("loadActiveWorkspace:stream_complete", {id, mode, focusId, elapsedMs: Date.now() - startedAt, token: controller?._loadToken})
                obj.joinChannel(obj.activeWorkspaceId)
                obj.loadedWorkspaceId = id
                obj.loadedWorkspaceMode = mode
                obj.loadedWorkspaceFocusId = mode === "flowinstance" ? focusId : undefined
                obj.logLoadDebug("loadActiveWorkspace:complete", {id, mode, focusId, token: controller?._loadToken})
            }catch(error){
                if( error?.name === "AbortError" ){
                    obj.logLoadDebug("loadActiveWorkspace:aborted", {id, mode, focusId, token: controller?._loadToken})
                    return
                }
                obj.logLoadDebug("loadActiveWorkspace:error", {id, mode, focusId, message: error?.message ?? `${error}`, token: controller?._loadToken})
                throw error
            }finally{
                obj.endPrimitiveLoad(controller)
                if( !background ){
                    obj.loadControl(true)
                    obj.updateLoadStatus({phase: 'idle', message: '', current: 0, total: 0})
                }
            }
            })

        },
        loadFlowInstanceById:async function(id, options = {}){
            obj.logLoadDebug("loadFlowInstanceById:called", {id, background: options.background === true})
            const existing = obj.primitive(id) ?? obj.primitiveByPlain(parseInt(id))
            if( existing?.workspaceId ){
                obj.logLoadDebug("loadFlowInstanceById:existing_workspace", {id, workspaceId: existing.workspaceId})
                await obj.loadActiveWorkspace(existing.workspaceId, {mode: "flowinstance", focusId: id, background: options.background === true})
                return obj.primitive(id) ?? obj.primitiveByPlain(parseInt(id))
            }
            obj.logLoadDebug("loadFlowInstanceById:fallback_loadWorkspaceFor", {id})
            await obj.loadWorkspaceFor(id, {mode: "flowinstance", focusId: id, background: options.background === true})
            return obj.primitive(id) ?? obj.primitiveByPlain(parseInt(id))
        },
        processServerActions( list ){
            list.forEach((entry)=>{
                if(entry.type === "new_primitives"){
                    const new_prims = []
                    const new_child = {}
                    const rel_updates = new Set()
                    for(const rData of entry.data){
                        obj.addPrimitive( rData )
                        const newObj = obj.primitive(rData.id)
                        new_prims.push( newObj )
                        rel_updates.add(newObj)
                    }
                    for(const rData of entry.data){
                        const newObj = obj.primitive(rData.id)
                        if( newObj.origin ){
                            new_child[newObj.origin.id] ||= []
                            new_child[newObj.origin.id].push(newObj)
                        }
                        for(const parentId of Object.keys(rData.parentPrimitives || {})){
                            const paths = (rData._parentPrimitives?.[parentId] ?? []).map(d =>d.startsWith('primitives.') ? d.slice('primitives.'.length) : d)
                            const parent = obj.primitive( parentId )
                            if( parent ){
                                rel_updates.add(parent)
                                paths.forEach((p)=>{
                                    parent.addRelationship( newObj, p, true)
                                })
                            }
                        }
                    }
                    obj.triggerCallback("new_primitive", new_prims, undefined, true )
                    obj.triggerCallback("relationship_update", Array.from( rel_updates ), undefined, true)
                    for(const [parentId, childList] of Object.entries( new_child )){
                        for(const child of childList ){
                            obj.triggerCallback("new_child", [parentId],{child}, true )
                        }
                    }
                }else if(entry.type === "add_relationship"){
                        const parent = obj.primitive( entry.id)
                        const target = obj.primitive( entry.target)
                        if( parent && target){
                            if(parent.primitives.fromPath(entry.path)?.allIds?.includes(entry.target)){
                            }else{
                                obj.triggerCallback("relationship_update", [entry.id, entry.target], {parent: entry.id, target:entry.target}, true)
                                parent.addRelationship(target, entry.path, true)
                            }
                        }
                    //console.log(  ` Add rel ${parent.id} > ${target.id} : ${entry.path}` )
                }else if(entry.type === "remove_relationship"){
                        const parent = obj.primitive( entry.id)
                        const target = obj.primitive( entry.target)
                        if( parent && target){
                            if(parent.primitives.fromPath(entry.path)?.allIds.includes(entry.target)){
                                obj.triggerCallback("relationship_update", [entry.id, entry.target], true)
                                parent.removeRelationship(target, entry.path, true)
                            }else{
                                console.log(`SKIP REMOVED - NOT THERE`)
                            }
                        }
                    //console.log(  ` Remove rel ${parent.id} > ${target.id} : ${entry.path}` )
                }else if(entry.type === "remove_primitives"){
                    if( entry.primitiveIds && Array.isArray(entry.primitiveIds) ){
                        obj.removePrimitive(undefined, entry.primitiveIds)
                    }
                }else if(entry.type === "workspace_added"){
                    console.log(`Adding workspace`)
                    const targetWorkspaceId = entry.id
                    obj.data.workspaces.push({
                        id: targetWorkspaceId,
                        _id: targetWorkspaceId,
                        ...entry.data
                    })
                    if( obj.activeUser?.info?.workspaces ){
                        obj.activeUser.info.workspaces.unshift( targetWorkspaceId )
                    }
                    obj.triggerCallback("workspace_added", [targetWorkspaceId], entry.data, true)
                }else if(entry.type === "workspace_updated"){
                    const targetWorkspaceId = entry.id
                    if( obj.workspace(targetWorkspaceId) ){
                        const existing = obj.data.workspaces.find(d=>d.id === targetWorkspaceId)
                        if( existing ){
                            for( const [k,v] of Object.entries( entry.data )){
                                existing[k]  = v
                            }
                        }
                        obj.triggerCallback("workspace_updated", [targetWorkspaceId], entry.data, true)
                    }
                }else if(entry.type === "organization_update"){
                    const targetOrganizationId = entry.id
                    const activeOrgId = obj.activeOrganization.id
                    obj.data.organizations[targetOrganizationId] = entry.data
                    obj.activeOrganization = obj.data.organizations[activeOrgId]
                    obj.triggerCallback("organization_updated", [targetOrganizationId], entry.data, true)

                }else if(entry.type === "set_fields"){
                    if( entry.fields){
                        const target = obj.primitive( entry.primitiveId)
                        if( target ){
                            Object.keys(entry.fields).forEach((field)=>{
                                const frag = field.split('.')
                                const root = frag.shift()
                                let val = entry.fields[field]

                                if( val === null){
                                    val = undefined
                                }

                                if( root === 'referenceParameters'){
                                    let trigger = false
                                    if(frag.length === 0){
                                        const nextParams = (val && typeof val === "object") ? val : {}
                                        const currentKeys = new Set(Object.keys(target.referenceParameters ?? {}))
                                        for( const f in nextParams){
                                            const oldValue = target.referenceParameters?.[f]
                                            target.setParameter(f, nextParams[f], true, true)
                                            if( !obj.deepEqual(oldValue, nextParams[f]) ){
                                                trigger = true
                                            }
                                            currentKeys.delete(f)
                                        }
                                        currentKeys.forEach((key)=>{
                                            if(target.referenceParameters){
                                                delete target.referenceParameters[key]
                                                trigger = true
                                            }
                                        })
                                    }else{
                                        const paramPath = frag.join(".")
                                        const oldValue = PrimitiveConfig.decodeParameter(target.referenceParameters, paramPath)
                                        if( val?.decode && val.modify !== undefined){
                                            target.modifyParameter(paramPath, val.value, val.modify, true)
                                        }else{
                                            target.setParameter(paramPath, val, true, true)
                                        }
                                        trigger = !obj.deepEqual(oldValue, val)
                                    }
                                    if( trigger ){
                                        obj.triggerCallback("set_parameter", [target], field, true )
                                    }
                                }else{

                                    const oldVal = PrimitiveConfig.decodeParameter(target, field)
                                    if( !obj.deepEqual(oldVal, val) ){
                                        target.setField(field, val, undefined, true)
                                        obj.triggerCallback("set_field", [target], field, true )
                                    }
                                }
                            })
                        }
                    }
                }
            })

        },
        ajaxResponseHandler(response){
            if( response.success){
                if( response.result && Array.isArray(response.result)){
                    this.processServerActions(response.result)
                }
                return true
            }            
            console.warn(response)
        },
        stateInfo: PrimitiveConfig.stateInfo,
        extendPath:function(path, ext){
            return this.stringToPath(this.pathToString(path) + "." + ext)
        },
        deepEqual:function(obj1, obj2){
            if (obj1 === obj2) return true;
          
            if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
              return false;
            }
          
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);
          
            if (keys1.length !== keys2.length) return false;
          
            for (let key of keys1) {
              if (!keys2.includes(key) || !obj.deepEqual(obj1[key], obj2[key])) {
                return false;
              }
            }
          
            return true;
          },
        pathToString:function(path){
            let out = []
            const nest = (node)=>{
                if( node instanceof Object ){
                    const k = Object.keys(node)[0]
                    out.push(k)
                    nest( node[k] )
                    return out
                }
                out.push(node)
                return out
            }
            return nest( path).join(".")
        },
        stringToPath:function(path){
            if( typeof(path) !== "string"){return undefined}
            let out = {}
            let current = out
            let nodes = path.split(".")
            let last = nodes.pop()
            if( nodes.length === 0){
                return last
            }
            let prev = undefined
            nodes.forEach((n,idx)=>{
                current[n] = (idx === nodes.length - 1) ? last : {}
                current = current[n]
            })            
            return out
        },
        controller: {
            async createMetric(primitive, object){
                return await this._createOrUpdateMetric( primitive, object )
            },
            async updatePrimitiveUserList(receiver, user, mode){
                const data = {
                    userId: user.id,
                    mode: mode
                }
                if( receiver.id === undefined || user.id === undefined){
                    return 
                }
                fetch(`/api/primitive/${receiver.id}/set_user`,{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    if( obj.ajaxResponseHandler( result )){
                        obj.triggerCallback('set_user', [receiver])
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )

            },
            async removeMetric(primitive, id){
                if( id === undefined ){ return id}

                const object = {id: id, type: "remove"}
                
                return await this._createOrUpdateMetric( primitive, object, "remove" )
            },
            async updateMetric(primitive, object, id){
                if( id === undefined ){ return id}

                object.id = id
                
                return await this._createOrUpdateMetric( primitive, object, "update" )
            },
            async _createOrUpdateMetric(primitive, object, mode = "add"){
                if( !object.type ||  primitive === undefined){
                    return undefined
                }
                const data = {
                    ...object,
                    primitive: primitive.id
                }
                let newId

                await fetch(`/api/${mode}_metric`,{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    if( obj.ajaxResponseHandler( result )){
                        console.log(result)
                        newId = result.id
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )
               return newId 
            },
            async createContact(object){
                if( !object.name ||  object.name.trim() === "" ){
                    return undefined
                }
                const data = {
                    data: object,
                }
                let newId

                await fetch("/api/add_contact",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    if( obj.ajaxResponseHandler( result )){
                        console.log(result)
                        newId = result.id
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )
               return newId 
            },
            async removePrimitive(object){
                const data = {
                    id: object.id,
                }
                let result = false
                await fetch("/api/remove_primitive",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (res) => {
                    if( obj.ajaxResponseHandler( res )){
                       result = res.result
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )
               return result 
            },
            async createPrimitive(object, parent, paths){


                let workspaceId = object.workspaceId || parent?.workspaceId || obj.activeWorkspaceId
                const data = {
                    parent: parent ? parent.id : undefined,
                    data: object,
                    workspaceId: workspaceId,
                    paths: paths
                }
                let newItem
                if( workspaceId === undefined){
                    throw "Must have an active workspace"
                }

                await fetch("/api/add_primitive",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    if( obj.ajaxResponseHandler( result )){
                        console.log(result)
                        newItem = result.result
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )
               return newItem
            },
            updateTitle:function( receiver, title){
                this.updateField(receiver, "title", title, "set_title", undefined)
            },
            updateParameter:function( receiver, parameterName, value ){
                this.updateField(receiver, `referenceParameters.${parameterName}`, value, "set_parameter", undefined)
            },
            modifyParameter:function( receiver, parameterName, value, add ){
                this.updateField(receiver, `referenceParameters.${parameterName}`, value, "set_parameter", add)
            },
            updateField( receiver, field, value, callback_name, modify){
                const data = {
                    receiver: receiver.id,
                    value: value,
                    modify: modify, 
                    field: field
                }
                obj.triggerCallback(callback_name, [receiver], field)
                fetch("/api/set_field",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    if( obj.ajaxResponseHandler( result )){
                        //obj.triggerCallback(callback_name, [receiver], field)
                    }
                  },
                  (error) => {
                    console.warn(error)
                  }
                )

            },
            moveRelationship( receiver, target, from, to ){
                const data = {
                    receiver: receiver.id,
                    target: target.id,
                    from: from,
                    to: to
                }
                obj.triggerCallback("relationship_update", [receiver, target])
                fetch("/api/move_relationship",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                    .then(
                    (result) => {
                        /*if( obj.ajaxResponseHandler( result )){
                            obj.triggerCallback("relationship_update", [receiver, target])
                        }*/
                    },
                    (error) => {
                        console.warn(error)
                    }
                    )
            },
            async setRelationshipAndWait( receiver, target, path, set ){
                const data = {
                    receiver: receiver.id,
                    target: target.id,
                    path: path,
                    set: set
                }
                obj.triggerCallback("relationship_update", [receiver, target])
                await fetch("/api/set_relationship",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
            },
            setRelationship( receiver, target, path, set ){
                const data = {
                    receiver: receiver.id,
                    target: target.id,
                    path: path,
                    set: set
                }
                obj.triggerCallback("relationship_update", [receiver, target])
                fetch("/api/set_relationship",{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                .then(res => res.json())
                .then(
                  (result) => {
                    /*if( obj.ajaxResponseHandler( result )){
                        obj.triggerCallback("relationship_update", [receiver, target])
                    }*/
                  },
                  (error) => {
                    console.warn(error)
                  }
                )
            }
        },
        metricResolver:{
                get(target, prop, receiver) {
                    if( prop in target.metric ){
                        const value = target.metric[prop];
                        if (value instanceof Function) {
                            return function (...args) {
                                return value.apply(target.metrics, args);
                            };
                        }
                        return value
                    }
                    if( prop === "analysis" ){
                        const collapse = true
                        const options = [
                            {
                                default: "Unknown", id: "contactExpertise", title: "By expertise", project: (item)=>{
                                    const contactId = item.referenceParameters?.contactId
                                    if( contactId === undefined){
                                        return undefined
                                    }
                                    return obj.contact(contactId)?.expertise
                                    
                                }
                            },
                            {
                                id: "contactDomain", title: "By domain expertise", project: (item)=>{
                                    const contactId = item.referenceParameters?.contactId
                                    if( contactId === undefined){
                                        return undefined
                                    }
                                    return obj.contact(contactId)?.domains
                                    
                                }
                            },
                            {
                                id: "companySector", title: "By company sector", project: (item)=>{
                                    const companyId = item.referenceParameters?.companyId
                                    if( companyId === undefined){
                                        return undefined
                                    }
                                    return obj.company(companyId)?.sector
                                    
                                }
                            },
                            {
                                default: "Unknown", id: "companyTurnover", title: "By company turnover", project: (item)=>{
                                    const companyId = item.referenceParameters?.companyId
                                    if( companyId === undefined){
                                        return undefined
                                    }
                                    let turnover = obj.company(companyId)?.turnover

                                    if( turnover === undefined){return "Unknown"}
                                    if( turnover.amount < 1000000){
                                        return "< $1m"
                                    }else if( turnover.amount < 10000000){
                                        return "$1m-$10m"
                                    }else if( turnover.amount < 100000000){
                                        return "$10m-$100m"
                                    }else if( turnover.amount < 500000000){
                                        return "$100m-$500m"
                                    }else if( turnover.amount < 1000000000){
                                        return "$500m-$1b"
                                    }else if( turnover.amount < 10000000000){
                                        return "$1b-$10b"
                                    }else if( turnover.amount >= 10000000000){
                                        return "$10b+"
                                    }
                                    
                                }
                            }
                        ]
                        let value = receiver.value
                        if( value === undefined){return undefined}
                        if( !(value instanceof Array)){
                            value = [value]
                        }

                        return options.map((option)=>{
                            let outcomes = value.map((v)=>{
                                if( v.list === undefined){return undefined}
                                let projections = v.list.reduce((o, d)=>{
                                    let projection = option.project(d)
                                    if( projection === undefined ){
                                        if( option.default ){
                                            projection = option.default
                                        }else{
                                            return o
                                        }
                                    }
                                    [projection].flat().forEach((p)=>{
                                        o.byP[p] = o.byP[p] || []
                                        o.byP[p].push(d)
                                        o.byI[d.id] = o.byI[d.id] || []
                                        o.byI[d.id].push(p)
                                    })
                                    return o
                                }, {byP:{}, byI:{}})

                                if( collapse ){
                                    const ordered = Object.keys(projections.byP).sort((a,b)=>projections.byP[b].length - projections.byP[a].length)
                                    const startTotal = Object.values(projections.byP).flat().length

                                    Object.keys(projections.byI).forEach((i)=>{
                                        projections.byI[i] = projections.byI[i].sort((a,b)=>ordered.indexOf(a) - ordered.indexOf(b))[0]
                                    })
                                    Object.keys(projections.byP).forEach((p)=>{
                                        projections.byP[p] = projections.byP[p].filter((item)=>projections.byI[item.id] === p)
                                        if(projections.byP[p].length === 0){
                                            delete projections.byP[p]
                                        }
                                    })
                                    const endTotal = Object.values(projections.byP).flat().length
                                }
                                projections = projections.byP


                                return Object.keys(projections).length > 0 ? {
                                    relationship: v.relationship,
                                    relationshipConfig: v.relationshipConfig,
                                    count: v.count,
                                    target: v.target,
                                    met: v.met,
                                    data: projections
                                } : undefined
                            }).filter((d)=>d)
                            return (outcomes && outcomes.length > 0) ? {
                                id: option.id,
                                title: option.title,
                                data: outcomes
                            } : undefined
                        }).filter((d)=>d)
                    }
                    if( prop === "value" ){
                        let metric = target.metric
                        let prims = target.parent.primitives.fromPath(metric.path)
                        let counts
                        let filter_empty = false


                        if( metric.type === "sum"){
                            if( prims === undefined){return 0}
                            return prims.allItems.map((p)=>parseInt(p.referenceParameters[metric.parameter] || 0)).reduce((a, c)=>a + c,0)
                        }

                        if( metric.type === "conversion" || metric.type === "count"){

                                if( metric.type === "conversion" ){
                                    if( prims === undefined){
                                        prims = {}
                                    }
                                    if( Object.keys(metric.path)[0] !== "results"){return undefined}
                                    
                                    let relationships = target.parent.metadata?.resultCategories[Object.values(metric.path)[0]].relationships
                                    Object.keys(relationships).forEach((k)=>{
                                        if( !Object.keys(prims).includes(k)){
                                            prims[k] = []
                                        }
                                    })
                                    
                                    counts = Object.keys(prims).map((k)=>({relationship: k, list: prims[k].allItems, count: prims[k].length, relationshipConfig: relationships[k]})).sort((a,b)=>relationships[b.relationship].order - relationships[a.relationship].order)
                                    counts = counts.map((v, idx, a)=>{
                                        if( idx > 0 ) {
                                            v.count += a[idx - 1].count
                                        }
                                        return v
                                    }).reverse()                            
                                }else if( metric.type === "count" ){
                                    if( prims === undefined){
                                        prims = {allIds: {length: 0}, allItems: []}
                                    }
                                    if( metric.targets && metric.targets.length > 0){
                                        counts = metric.targets.map((t)=>{
                                            if( t.presence ){
                                                return {presence: true, count: prims.allIds.length, list: prims.allItems}
                                            }else{
                                                if( !Array.isArray(prims) ){
                                                    const k = t.relationship
                                                    if( prims[k] ){
                                                        return {relationship: k, list: prims[k].allItems, count: prims[k].length}

                                                    }
                                                }
                                            }
                                        }).filter((d)=>d)

                                    }else{
                                        counts = [{count: prims.allIds.length, list: prims.allItems}]
                                    }
                                    let by_reln = metric.targets || metric.by_relationship
                                    filter_empty = true
                                }
                            if( metric.targets && metric.targets.length > 0 ){
                                counts = counts.filter((d)=>{
                                    let mt = metric.targets.find((d2)=>(d2.presence === d.presence && d2.presence !== undefined) || (d2.relationship === d.relationship))
                                    if( mt ){
                                        d.target = mt.value
                                        d.met = d.count >= d.target
                                        return true
                                    }
                                    return !filter_empty
                                })
                            }
                            return counts
                        }
                    }
                }
        },
        deletePrimitive:function(id){
            delete obj.data.primitives[ id ]
        },
        addPrimitive:function(data){
            obj.data.primitives[data.id || data._id] = primitive_access(data,"primitive")
        },
        templates:function(){
            return Object.values(obj.data.templates)
        },
        template:function(id){
            return obj.data.templates[id]
        },
        fetchWorkspaceTemplates: async function(workspaceId){
            const target = workspaceId ?? obj.activeWorkspaceId
            if(!target){
                return []
            }
            const key = `${target}`
            if(!key){
                return []
            }
            const response = await fetch(`/api/templates?workspace=${encodeURIComponent(key)}`)
            if(!response.ok){
                throw new Error(`Failed to fetch workspace templates (${response.status})`)
            }
            const data = await response.json()
            if(!Array.isArray(data)){
                return []
            }
            return data.map((raw)=>{
                if(!raw){
                    return raw
                }
                const template = primitive_access( raw, "primitive")
                const id = template.id ?? template._id
                return {
                    ...template,
                    id,
                    isTemplate: template.isTemplate ?? true
                }
            })
        },
        primitives:function(){
            return Object.values(obj.data.primitives)
        },
        primitiveByPlain:function(id){
            let data = obj.primitives().find((p)=>p.plainId === id)
            return data
        },
        primitive:function(id){
            let data = obj.data.primitives[id]
            if( !data && !isNaN(id) ){
                data = this.primitiveByPlain(parseInt(id))
            }
            return data
        },
        workspace:function(id){
            return obj.workspaces().find((d)=>d._id === id)
        },
        workspaces:function(){
            return obj.data.workspaces
        },
        categories:function(){
            return Object.values(obj.data.categories)
        },
        category:function(id){
            return obj.data.categories[ id ]
        },
        companies:function(){
            return this.data.companies
        },
        company:function(id){
            return this.companies().find((d)=>d.id === id)
        },
        contacts:function(){
            return this.data.contacts
        },
        contact:function(id){
            if( id === undefined || id === null){return undefined}
            let out = this.contacts().find((d)=>d.id === id) 
            if( out === undefined){
                out = this.contacts().find((d)=>d.plainId === id)
                if( out ){
                    console.log(`Found contact by old id`)
                }
            }
            return out
        },
        user:function(id){
            return this.users().find((d)=>d._id === id)
        },
        users:function(){
            return this.data.users
        },
        framework:function(id){
            return this.frameworks().find((d)=>d.id === id)
        },
        frameworks:function(){
            return this.data.frameworks
        },
        deregisterCallback:function(id){
            let store = this
            Object.keys(store.callbacks).forEach((key)=>{
                store.callbacks[key] = store.callbacks[key].filter((e)=>e.id !== id)
            })
        },     
        registerCallback:function(id, events, cb, idList){
            let store = this
            if( id === null || id === undefined){
                this.callback_tracker = (this.callback_tracker || 0) + 1
                id = this.callback_tracker
            }
            if( typeof(events) === "string"){
                events = events.split(" ")
            }
            ;[events].flat().forEach((e)=>{
                if( store.callbacks[e] === undefined){
                    store.callbacks[e] = []
                }
                store.callbacks[e] = store.callbacks[e].filter((d)=>d.id !== id)
                let flatIds, descendIds 
                if( idList ){
                    for(const rawId of [idList].flat(Infinity)){
                        if( !rawId ){
                            continue
                        }
                        if( typeof rawId === "string" && rawId.endsWith("+")){
                            descendIds ||= []
                            descendIds.push( rawId.slice(0, rawId.length - 1))
                        }else{
                            flatIds ||= []
                            flatIds.push(rawId)
                        }
                    }
                }
                store.callbacks[e].push({callback: cb, filterIds: flatIds, descendIds, id: id})
                //console.log(`registered ${e} for ${id} (${store.callbacks[e].length}) / ${[idList].flat().join(", ")}`)
            })
            return id
        },
        triggerCallback:function(e, items, data, fromRemote){
            let store = this
            if( this.callbacks[e] === undefined){
                return
            }
            items = [items].flat()

            const ids = items.map((d)=> d instanceof Object ? d.id : d)
            items = items.map((d)=> d instanceof Object ? d : obj.primitive(d))
            if( this.loadDebugEnabled && this.loadDebugEnabled() && ["new_primitive", "relationship_update", "new_child", "delete_primitive", "set_field", "set_parameter"].includes(e) ){
                this.logLoadDebug("triggerCallback", {
                    event: e,
                    listenerCount: this.callbacks[e]?.length ?? 0,
                    idCount: ids.length,
                    ids: ids.slice(0, 10),
                    fromRemote: fromRemote === true
                })
            }

            let name = e

            this.callbacks[e].forEach((listener)=>{
                let doCall = true
                if( listener.filterIds && listener.filterIds.length > 0 ){
                    const matchesFilter = ids.some((item)=>listener.filterIds.includes(item))
                    if( !matchesFilter ){
                        doCall = false
                    }
                }
                if( doCall && listener.descendIds && listener.descendIds.length > 0 ){
                    const matchesDescendant = items.some((item)=>{
                        const allIds = item?.primitives?.uniqueAllIds ?? []
                        return allIds.some((childId)=>listener.descendIds.includes(childId))
                    })
                    if( !matchesDescendant ){
                        doCall = false
                    }
                }
                if( doCall){
                    listener.callback(ids, name, data, fromRemote, items)
                }
            })

        },
        queryPrimitives:async function (primitive, params){
            let url = `/api/primitive/${primitive.id}/queryPrimitives`

            if(params){
                for(const k in params){
                    if( params[k] === undefined){
                        delete params[k]
                    }
                }
                url += '?' + new URLSearchParams(params)
            }

            let out

            const result = await fetch(url,{
                method: "GET",
            })
            const response = await result.json()
            return response

        },
        doPrimitiveAction:async function (primitive, action, params, callback){
            let url = `/api/primitive/${primitive.id}/action/${action}`

            const result = await fetch(url,{
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(params)
            })
            const response = await result.json()
            const success = this.ajaxResponseHandler(response)
            if (response?.result?.debugToConsole) {
                const label = response.result?.label ?? `Action ${action} debug payload`
                console.log(label, response.result?.payload ?? response.result)
            }
            if( response.success && callback && typeof(callback) === "function"){
                await callback(response.result)
            }
            return success

        },
        doPrimitiveDocumentDiscovery:async function ( primitive){
            
            let url = `/api/primitive/${primitive.id}/discover`

                const result = await fetch(url,{
                    method: "GET",
                })
                const response = await result.json()
                return response
        },
        doPrimitiveDocumentQuestionsAnalysis:async function ( primitive, options ){
            let url = `/api/primitive/${primitive.id}/analyzeQuestions`
            url += '?' + new URLSearchParams(options)

            const result = await fetch(url,{
                method: "GET",
            })
            const response = await result.json()
            return response
        },
        getPrimitiveDocumentAsText:async function ( primitive ){
            let revision = ''
            if( primitive.referenceParameters?.notes?.lastFetched ){
                revision = '?rev=' + new Date(primitive.referenceParameters?.notes?.lastFetched).getTime()
            }
            const url = `/api/primitive/${primitive.id}/getDocumentAsPlainText${revision}`
                const result = await fetch(url
                ,{
                    method: "GET",
                })
                const response = (await result.json())?.result
                return response
        },
        getPrimitiveDocument:async function ( primitive ){
            let revision = ''
            if( primitive.referenceParameters?.notes?.lastFetched ){
                revision = '?rev=' + new Date(primitive.referenceParameters?.notes?.lastFetched).getTime()
            }
            const url = `/api/primitive/${primitive.id}/getDocument${revision}`
                const result = await fetch(url
                ,{
                    method: "GET",
                })
                const response = await result.arrayBuffer()
                return response
        },
        createContact:async function(data){
            const newId = await this.controller.createContact(data)
            data.id = newId
            this.data.contacts = this.data.contacts.filter((d)=>d.id !== newId)
            this.data.contacts.push(data)
            return data
        },
        removePrimitive:async function(primitive, skip = false){
            if( !skip ){
                if(!(primitive instanceof Object)){
                    primitive = this.primitive(primitive)
                }
                console.log(`removing ${primitive.id}`)
            }
            let removedIds
            if(!skip){
                removedIds = await this.controller.removePrimitive(primitive) 
            }else{
                removedIds = skip
            }
            if( removedIds ){
                //console.log(`Server deleted ${removedIds.length} items`)
                const notifyIds = []
                for( const targetId of removedIds ){
                    const target = this.primitive(targetId)
                    if( target ){
                        target.parentPrimitives.forEach((parent)=>{
                            const rels = target.parentPaths(parent.id)
                            notifyIds.push(parent.id)
                            rels.forEach((path)=>{
                                parent.primitives.remove( target.id, path)
                            })
                        })
                        this.deletePrimitive( target.id )
                    }
                }
                obj.triggerCallback("relationship_update", notifyIds )
                obj.triggerCallback("delete_primitive", removedIds )
            }else{
                console.warn(`Couldn't remove ${primitive.id}`)
                throw new Error("Error removing")
            }
        },
        createPrimitive:async function( options ){
            let category = options.categoryId ? this.category( options.categoryId ) : undefined
            let {
                title = undefined,//category?.blankTitle ? undefined : "New item", 
                type = "result", 
                state = undefined, 
                extraFields = {}, 
                parent = undefined, 
                flowElement = undefined,
                workspaceId = undefined, 
                parentPath = undefined, 
                categoryId = undefined, 
                referenceParameters = {} } = options
            
            if(!categoryId && options.referenceId){
                categoryId = options.referenceId 
            }

            let paths = []
            if( parent ){
                paths.push( "origin" )

            }
            if( parentPath){
                paths.push( parentPath)
            }
            
            const config = PrimitiveConfig.typeConfig[type]
            if( config?.createAtWorkspace){
                paths = paths.filter((p)=>p !== 'origin')
            }

            if( type === "prompt"){
                if( category == undefined){
                    throw new Error(`Cant add prompt without a category`)
                }
                if( !(parent && parent.type === "question")){
                    throw new Error(`Cant add prompt ${parent ? `to parent of type ${parent.type}` : 'without parent'}`)
                }
            }

        //    if( type === "result"){
                if( parent && parent.metadata && parent.metadata.resultCategories){
                    let match
                    if( category){
                        match = parent.metadata.resultCategories.find((d)=>d.resultCategoryId === categoryId)
                    }
                    if( !match ){
                        match = parent.metadata.resultCategories.find((d)=>d.type === type) 
                    }
                    console.log(`PATHS`, match)
                    if( match ){
                        if( match.relationships ){
                            paths.push({results: {[match.id]: Object.keys(match.relationships)[0]}})
                        }else{
                            paths.push({results: match.id})
                        }
                    }                    
/*                }else{
                    throw new Error(`Cant add result with category ${categoryId} to Prim #${parent.plainId}`)*/
                }
          //  }

            let data = {
                title: title,
                type: type,
                state: state,
                flowElement,
                workspaceId: workspaceId,
                primitives: {},
                referenceId: categoryId,
                referenceParameters: referenceParameters,
                ...extraFields
            }
            /*const newIds = await this.controller.createPrimitive(data, parent, paths)
            if( newIds === undefined){
                console.warn('New primitive not created')
                return
            }
            data._id = newIds.id
            data.plainId = newIds.plainId
            data.workspaceId = obj.activeWorkspaceId
            */
            const rData = await this.controller.createPrimitive(data, parent, paths)
            if( rData === undefined){
                console.warn('New primitive not created')
                return
            }
            this.addPrimitive( rData )
            const newObj = obj.primitive(rData.id)
            
            if( parent ){
                paths.forEach((p)=>{
                    parent.addRelationship( newObj, p, true)
                })
                obj.triggerCallback("relationship_update", [parent, newObj], {parent: parent.id, target: newObj.id})
            }
            obj.triggerCallback("new_primitive", [newObj] )
            return  newObj
        }
    }

    obj.socket = io(window.location.hostname === "localhost" ? "http://localhost:3001" : undefined, {
        withCredentials: true
    })
    obj.socket.on('control', (data)=>{
        obj.joinChannel( obj.activeWorkspaceId )
    })
    obj.socket.on('message', (data)=>{
        let items = data
        if( !Array.isArray(data) ){
            items = data.data
            if( data.track ){
                if (data.text) {
                    console.info(`[server:${data.track}] ${data.text}`);
                }
            }
        }
        obj.processServerActions(items)
    })

    obj.structure = PrimitiveParser(obj)
    obj.referenceParametersParser = {
        set(d, prop, value, receiver) {
            d[prop] = value
            if( prop === "contact" || prop === "contactId" ){
                d._contact = undefined
            }
            return true
        },
        get(d, prop, receiver) {
            if( prop === "contactName" ){
                return receiver.contact?.name
            }
            if( prop === "contact" && ("contactId" in d)){
                if( d._contact === undefined){
                    d._contact = obj.contact(d.contactId)
                }
                return d._contact
            }
            if( prop in d){
                return d[prop]
            }
        }
    }

    if( !prims ){
        instance = obj
    }
    obj.uniquePrimitives = uniquePrimitives
    obj.followSteps = (out, steps)=>{
        for(const d of steps){
            out = doStep(out, d)
        }
        return out
    }
    const equalRelationships = (or1,or2)=>{
        if( or1 === or2){
            return true
        }
        let r1 = [or1].flat()
        let r2 = [or2].flat()
    
        if(r1.length !== r2.length){
            return false
        }
        const setR2 = new Set(r2);

        return r1.every(element => setR2.has(element));

    }
    obj.equalRelationships = equalRelationships
    let actionPrototype;
    const getActionPrototype = ()=>{
        if( actionPrototype ){
            return actionPrototype
        }
        const proto = {}
        Object.entries(actions).forEach(([key, handler])=>{
            if( key === "id"){
                return
            }
            Object.defineProperty(proto, key, {
                configurable: true,
                enumerable: false,
                get(){
                    const target = this
                    return handler(target, this, obj)
                }
            })
        })
        actionPrototype = proto
        return actionPrototype
    }

    const primitive_access = (d, type)=>{
        if( d._id){
            d.id = d._id
        }
        if( !d.primitives ){
            d.primitives = {}
        }
        const rawParentPrimitives = d._parentPrimitives || d.parentPrimitives || {}
        d._parentPrimitives = rawParentPrimitives
        if( Object.prototype.hasOwnProperty.call(d, "parentPrimitives")){
            delete d.parentPrimitives
        }
        d[MASTER_TYPE] = type
        d[TITLE_VALUE] = d.title
        d[TITLE_SKIP_FLAG] = false
        Object.defineProperty(d, "title", {
            configurable: true,
            enumerable: true,
            get(){
                return this[TITLE_VALUE]
            },
            set(value){
                this[TITLE_VALUE] = value
                if( this[TITLE_SKIP_FLAG] ){
                    this[TITLE_SKIP_FLAG] = false
                    return value
                }
                obj.controller.updateTitle(this, value)
                return value
            }
        })
        const hasReferenceParameters = Object.prototype.hasOwnProperty.call(d, "referenceParameters")
        const initialReferenceParameters = hasReferenceParameters ? d.referenceParameters : undefined
        if( hasReferenceParameters ){
            delete d.referenceParameters
        }
        d[RAW_REFERENCE_PARAMETERS] = initialReferenceParameters
        if( !d._referenceParameters ){
            d._referenceParameters = initialReferenceParameters ?? {}
        }
        Object.defineProperty(d, "referenceParameters", {
            configurable: true,
            enumerable: true,
            writable: true,
            value: new Proxy(d._referenceParameters, obj.referenceParametersParser)
        })
        const primitiveStore = getPrimitiveStore(d) || d.primitives || {}
        if( !Object.prototype.hasOwnProperty.call(d, RAW_PRIMITIVES) ){
            Object.defineProperty(d, RAW_PRIMITIVES, {
                configurable: true,
                enumerable: false,
                writable: true,
                value: primitiveStore
            })
        }else{
            d[RAW_PRIMITIVES] = primitiveStore
        }
        delete d.primitives
        Object.defineProperty(d, "primitives", {
            configurable: true,
            enumerable: true,
            get(){
                return actions.primitives(d, this, obj)
            },
            set(value){
                const next = value || {}
                if( !Object.prototype.hasOwnProperty.call(d, RAW_PRIMITIVES) ){
                    Object.defineProperty(d, RAW_PRIMITIVES, {
                        configurable: true,
                        enumerable: false,
                        writable: true,
                        value: next
                    })
                }else{
                    d[RAW_PRIMITIVES] = next
                }
            }
        })

        const prototype = getActionPrototype()
        Object.setPrototypeOf(d, prototype)
        return d
    }
    obj.consumePrimitiveStream = async function(response, options = {}){
        const {reset = false, trackProgress = true} = options
        const store = this
        if( !response?.ok ){
            if( trackProgress ){
                store.updateLoadStatus({phase: 'primitives', message: `Failed to load primitives (${response?.status})`})
            }
            throw new Error(`Failed to load primitives (${response?.status})`)
        }
        if( reset ){
            store.data.primitives = {}
        }else{
            store.data.primitives ||= {}
        }

        const updateProgress = (update = {}) => {
            if( !trackProgress ){
                return
            }
            store.updateLoadStatus({phase: 'primitives', ...update})
        }

        const normaliseItem = (item) => {
            if( !item ){
                return undefined
            }
            if( item._id && typeof item._id === 'object' && item._id.$oid ){
                item._id = item._id.$oid
            }else if( item._id && typeof item._id !== 'string'){
                try{
                    item._id = item._id.toString()
                }catch(e){
                }
            }
            return item
        }

        const applyBatch = (items = []) => {
            for(const item of items){
                const normalised = normaliseItem(item)
                if( !normalised ){
                    continue
                }
                store.data.primitives[normalised._id] = primitive_access(normalised, "primitive")
            }
        }

        const contentType = response.headers.get('content-type') || ''
        updateProgress({message: 'Loading primitives...', current: 0, total: 0})

        let total = 0
        let loaded = 0

        const handlePayload = (payload) => {
            if( !payload || typeof payload !== 'object'){
                return
            }
            switch(payload.type){
                case 'meta':
                    if( payload.total !== undefined ){
                        total = payload.total
                    }
                    updateProgress({message: total ? `Loading primitives (${loaded}/${total})` : 'Loading primitives...', current: loaded, total})
                    break
                case 'batch':{
                    const items = Array.isArray(payload.items) ? payload.items : []
                    applyBatch(items)
                    loaded += items.length
                    updateProgress({message: total ? `Loading primitives (${loaded}/${total})` : `Loaded ${loaded} primitives`, current: loaded, total})
                    break
                }
                case 'end':
                    if( payload.total !== undefined && !total ){
                        total = payload.total
                    }
                    updateProgress({message: total ? `Loaded ${loaded}/${total} primitives` : `Loaded ${loaded} primitives`, current: loaded, total: total || payload.total || loaded})
                    break
                default:
                    break
            }
        }

        if( contentType.includes('application/msgpack') ){
            const unpackr = new Unpackr({ mapsAsObjects: true })
            let pending = new Uint8Array(0)

            const appendChunk = (chunk)=>{
                if( !chunk || chunk.length === 0 ){
                    return
                }
                const incoming = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk)
                if( pending.length ){
                    const merged = new Uint8Array(pending.length + incoming.length)
                    merged.set(pending, 0)
                    merged.set(incoming, pending.length)
                    pending = merged
                }else{
                    pending = incoming
                }

                let offset = 0
                while((pending.length - offset) >= 4){
                    const frameSize = new DataView(pending.buffer, pending.byteOffset + offset, 4).getUint32(0)
                    const available = pending.length - offset - 4
                    if( available < frameSize ){
                        break
                    }
                    const start = offset + 4
                    const end = start + frameSize
                    const payloadBytes = pending.slice(start, end)
                    try{
                        const payload = unpackr.unpack(payloadBytes)
                        handlePayload(payload)
                    }catch(err){
                        console.error('Failed to unpack primitives frame', err)
                    }
                    offset = end
                }

                if( offset > 0 ){
                    pending = pending.slice(offset)
                }
            }

            if( response.body && typeof response.body.getReader === 'function'){
                const reader = response.body.getReader()
                try{
                    while(true){
                        const {value, done} = await reader.read()
                        if( done ){
                            break
                        }
                        appendChunk(value)
                    }
                } finally {
                    reader.releaseLock?.()
                }
            }else{
                appendChunk(new Uint8Array(await response.arrayBuffer()))
            }

            if( pending.length ){
                // Incomplete frame remains
                console.warn('Discarding incomplete msgpack frame for primitives stream')
            }

            if( trackProgress && total === 0 ){
                updateProgress({message: `Loaded ${loaded} primitives`, current: loaded, total: loaded})
            }
            return loaded
        }

        if( !response.body || typeof response.body.getReader !== 'function' ){
            const data = await response.json().catch(()=>[])
            const list = Array.isArray(data) ? data : (data?.items ?? [])
            applyBatch(list)
            updateProgress({current: list.length ?? 0, total: list.length ?? 0, message: `Loaded ${list.length ?? 0} primitives`})
            return list.length ?? 0
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let bufferText = ''

        try{
            while(true){
                const {value, done} = await reader.read()
                if( done ){
                    break
                }
                bufferText += decoder.decode(value, {stream: true})
                const parts = bufferText.split('\n')
                bufferText = parts.pop() ?? ''
                for(const part of parts){
                    const trimmed = part.trim()
                    if( !trimmed ){
                        continue
                    }
                    try{
                        handlePayload(JSON.parse(trimmed))
                    }catch(err){
                        console.error('Failed to parse primitive chunk', err)
                    }
                }
            }
        } finally {
            reader.releaseLock?.()
        }

        const remaining = bufferText.trim()
        if( remaining ){
            try{
                handlePayload(JSON.parse(remaining))
            }catch(err){
                console.error('Failed to parse trailing primitive chunk', err)
            }
        }

        if( trackProgress && total === 0 ){
            updateProgress({message: `Loaded ${loaded} primitives`, current: loaded, total: loaded})
        }

        return loaded
    }
    obj.data = {
        primitives:{}
    }
    if( prims ){
        obj.data = {
            primitives: prims.reduce((a,d)=>{a[d.id] = primitive_access(d, "primitive");return a}, {})
        }
    }

    obj.processOutstandingDiscovery = async function(){
        obj.primitives().forEach((primObj)=>{
            if( primObj.type === "result"){
                if( primObj.referenceParameters.notes !== undefined ){
                    if( primObj.origin && primObj.origin.doDiscovery ){
                        if(primObj.discoveryDone !== true ){
                            primObj.setLocalFlag("doingDiscovery", true)
                            primObj.analyzer().doDiscovery()
                        }
                    }
                }
            }
        })
    }
    

    obj.loadData = async function(){
        obj.loadProgress = []
        obj.updateLoadStatus({phase: 'boot', message: 'Checking account status...', current: 0, total: 0})
        const status = await fetch('/api/status').then(response => response.json())
        if( !status.logged_in ){
            if( window.location.pathname !== "/signup" && !window.location.pathname.startsWith("/reset") && window.location.pathname !== "/login" && !window.location.pathname.startsWith("/published")){
                const desiredPath = `${window.location.pathname}${window.location.search ?? ''}${window.location.hash ?? ''}` || '/';
                window.location.href = `/login?redirect=${encodeURIComponent(desiredPath)}`
            }
            obj.data.categories = []
            obj.data.primitives = {}
            obj.activeUser = {}
            obj.data.workspaces = []
            obj.data.users = []
            obj.data.organizations = []
            obj.data.frameworks = []
            obj.data.templates = {}
            obj.updateLoadStatus({phase: 'idle', message: '', current: 0, total: 0})
            return
        }
        obj.activeUser = status.user
        obj.env = status.env

        const fetchTargets = [
            ['users', '/api/users'],
            ['categories', '/api/categories'],
            ['workspaces', '/api/workspaces'],
            ['templates', '/api/templates'],
            ['organizations', '/api/organizations']
        ]
        let completed = 0
        obj.updateLoadStatus({phase: 'boot', message: 'Loading account data...', current: 0, total: fetchTargets.length})

        const results = await Promise.all(fetchTargets.map(async ([key, url]) => {
            const response = await fetch(url)
            const data = await response.json()
            obj.loadProgress.push(key)
            completed += 1
            obj.updateLoadStatus({phase: 'boot', message: `Loaded ${key}`, current: completed, total: fetchTargets.length})
            return [key, data]
        }))

        const dataMap = Object.fromEntries(results)
        const users = dataMap.users ?? []
        const categories = dataMap.categories ?? []
        const workspaces = dataMap.workspaces ?? []
        const templates = dataMap.templates ?? []
        const organizations = dataMap.organizations ?? []

        obj.data.users = users.map((d)=>{
            return {...d, id: d._id}
        })
        obj.data.categories = categories.reduce((o,d)=>{o[d.id] = d; return o}, {})
        obj.data.primitives = {}
        obj.data.organizations = organizations.reduce((a,c)=>{a[c.id] = c; return a}, {})
        obj.data.workspaces = workspaces.map((d)=>{d.id = d._id; return d})
        obj.data.templates = templates.reduce((a,c)=>{
            c.id = c._id
            c.isTemplate = true
            a[c.id] = c
            return a
        }, {})

        obj.activeUser.info = obj.users().find((d)=>d._id === obj.activeUser._id)
        obj.activeUser.id = obj.activeUser._id

        obj.activeOrganization = Object.values(obj.data.organizations)[0]
        const roleInOrg = obj.activeOrganization?.members?.find(d=>d.userId === obj.activeUser.id)?.role ?? "guest"
        obj.activeUser.role = roleInOrg

        obj.updateLoadStatus({phase: 'boot', message: 'Core data loaded', current: completed, total: fetchTargets.length})
        return true
    }
    obj.refreshUser = async function(){
        await fetch('/api/refresh')
            .then(response => response.json())
            .then((response)=>{
                obj.activeUser = response.user
                obj.activeUser.id = obj.activeUser._id
                obj.activeUser.info = obj.users().find((d)=>d.email === obj.activeUser.email)
                console.log(`updated user`)
            })
    }
    
    return obj
}



export default MainStore
